import {
  applyConnectedCharacterValues,
  isConnectedCharacterField,
  isConnectionSourceField,
} from "../../../../../../../utils/fieldConnections";
import ConnectedCharacterInputs from "../../../../../../permitForms/ConnectedCharacterInputs";
import {
  useEffect,
  useState,
  useCallback,
  useRef,
  memo,
  useMemo,
  createContext,
  useContext,
} from "react";
import { useSelector } from "react-redux";
import { isPlainObject } from "lodash";
import axios from "axios";

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

import {
  Stack,
  Box,
  Typography,
  TextField,
  Grid2 as Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Autocomplete,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Button,
  Chip,
  Popover,
  Tooltip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { getSections } from "../../../../../../permitForms/permitFormHelpers";
import {
  formatMoneyValue,
  formatPercentageValue,
  parseDollarStrToNumber,
} from "../../../../../../../functions/permitFormHelpers";
import { getFormulaUpdate } from "../../../../../../../utils/formulaFields";
import {
  hiddenFields,
  filter_number_input_values,
  unitNumMapping,
  totalFields,
} from "../../../../../../../data/permitFormsData";
import {
  bondingLabel,
  customLabels,
  feeSimpleLabel,
  mortgageLabel,
  privateProviderLabel,
  projectLabel,
} from "../../../../../../../data/buildingPermit/fieldDatas";
import RenderPrivateProvider from "./savedProjects/RenderPrivateProvider";
import RenderProjectContact from "./savedProjects/RenderProjectContact";
import RenderDesignPro from "./savedProjects/RenderDesignPro";
import RenderFeeSimple from "./savedProjects/RenderFeeSimple";
import RenderBonding from "./savedProjects/RenderBonding";
import RenderMortgage from "./savedProjects/RenderMortgage";
import RenderTermite from "./savedProjects/RenderTermite";
import { getTemplateCategoryConfig } from "../../../../../../permitForms/utils/templateCategoryHelpers";
import { scrollPreviewFieldIntoView } from "./previewFieldScroll";
import { updatePreviewFieldInstanceLabel } from "./previewFieldLabel";
import { isRightPreviewFormField } from "./previewFieldSelection";
import GenericTablePreview, {
  isGenericTablePreviewField,
} from "./GenericTablePreview";
import {
  isTextConnectedField,
  applyConnectedTextValues,
} from "../../../../../../../utils/textFieldConnections";
import SortableBlock from "./SortableBlock";

const toTitleCase = (str = "") =>
  String(str)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const TEMPLATE_CATEGORY_SECTION_PREFIX = "template-category:";
const TEMPLATE_SELECTOR_FIELD_TYPES = new Set([
  "privateProvider",
  "project_contact_details",
  "designProfessional",
  "feeSimple",
  "bonding",
  "mortgage",
]);
const TEMPLATE_CATEGORY_FIELD_LABELS = {
  ...privateProviderLabel,
  ...projectLabel,
  ...customLabels,
  ...feeSimpleLabel,
  ...bondingLabel,
  ...mortgageLabel,
};

export const getPreviewFieldLabel = (field) => {
  const fieldName = String(field?.name || "").trim();
  const assignedLabel = [field?.field_label, field?.label]
    .map((label) => String(label || "").trim())
    .find((label) => label && label !== fieldName);

  return (
    assignedLabel ||
    TEMPLATE_CATEGORY_FIELD_LABELS[fieldName] ||
    toTitleCase(fieldName) ||
    fieldName
  );
};

export const normalizeTemplateCategoryPreviewField = (field) => {
  const templateCategory = getTemplateCategoryConfig(field?.category_id);

  if (!templateCategory) {
    return field;
  }

  return {
    ...field,
    label: getPreviewFieldLabel(field),
    ...(TEMPLATE_SELECTOR_FIELD_TYPES.has(field?.type)
      ? {
          field_type: "text",
          type: "text",
        }
      : {}),
  };
};

export const getPreviewSectionTitle = (item) => {
  const fieldCategoryId = item?.fields?.find((field) =>
    getTemplateCategoryConfig(field?.category_id),
  )?.category_id;
  const sectionCategoryId = String(item?.section || "").startsWith(
    TEMPLATE_CATEGORY_SECTION_PREFIX,
  )
    ? String(item.section).slice(TEMPLATE_CATEGORY_SECTION_PREFIX.length)
    : "";
  const templateCategory = getTemplateCategoryConfig(
    fieldCategoryId || sectionCategoryId,
  );

  return (
    templateCategory?.label ||
    toTitleCase(item?.fields?.[0]?.block_label || item?.section || "N/A")
  );
};

export const normalizePreviewSections = ({
  sections = [],
  directItems = [],
  allFields = [],
}) => ({
  sections: sections.map((section) => ({
    ...section,
    fields: section.fields.map(normalizeTemplateCategoryPreviewField),
  })),
  directItems: directItems.map(normalizeTemplateCategoryPreviewField),
  allFields: allFields.map(normalizeTemplateCategoryPreviewField),
});

export const isHiddenPreviewField = (field) =>
  hiddenFields?.includes(field?.name) &&
  !getTemplateCategoryConfig(field?.category_id);

const deriveLabel = (field) => getPreviewFieldLabel(field);

const getDemoValue = (field, formField) => {
  const key = (field.name || "").toLowerCase();
  const lbl = deriveLabel(field).toLowerCase();
  const combined = key + " " + lbl;

  if (formField?.field_type === "selectGroup")
    return formField?.options?.[0] ?? null;

  if (/amount|cost|fee|price|total|subtotal|rate|value/.test(combined))
    return formField?.field_type === "number" ? 1500 : "$1,500.00";

  if (/hour|minute|hours|second/.test(combined)) return 1;

  if (/sq|area|footage|sqft|ftg/.test(combined))
    return formField?.field_type === "number" ? 1200 : "1,200";

  if (formField?.field_type === "number") return 1;

  if (/date|expir|issued|start|end/.test(combined)) return "01/15/2025";

  if (/phone|mobile|fax/.test(combined)) return "555-867-5309";

  if (/email/.test(combined)) return "sample@example.com";

  if (/zip|postal/.test(combined)) return "33101";

  if (/\bstate\b/.test(combined)) return "FL";

  if (/\bcity\b/.test(combined)) return "Miami";

  if (/county/.test(combined)) return "Miami-Dade";

  if (/address|street|addr/.test(combined)) return "123 Main Street";

  if (/unit|apt|suite|ste/.test(combined)) return "Suite 100";

  if (/owner|applicant|signer|qualifier|signee/.test(combined))
    return "John A. Smith";
  if (/contractor|company|business|corp|firm/.test(combined))
    return "ABC Construction LLC";
  if (/\bname\b/.test(combined)) return "Jane Doe";

  if (/license|permit|application|num|number/.test(combined))
    return "LIC-2025-001";

  if (/desc|note|comment|work|scope|detail/.test(combined))
    return "Sample description of work to be performed.";

  if (/project|package|lot|block/.test(combined)) return "Sample Project A";

  if (/folio|parcel/.test(combined)) return "01-2345-678-9000";

  return deriveLabel(field) || "Sample Text";
};

const handleMoneyChange = (event, setLocalValue) => {
  // Allow only numeric input and the decimal point during typing
  let { value } = event.target;
  value = parseDollarStrToNumber(value);

  setLocalValue(value);
};

const handleDateChange = (event, setLocalValue) => {
  let { value } = event.target;
  value = value.replace(/\D/g, ""); // Remove all non-digit characters

  // Format the value as mm/dd/yyyy
  let formattedValue = "";
  if (value.length > 4) {
    formattedValue = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(
      4,
    )}`;
  } else if (value.length > 2) {
    formattedValue = `${value.slice(0, 2)}/${value.slice(2)}`;
  } else {
    formattedValue = value;
  }

  // Validate and set the formatted value
  setLocalValue(formattedValue);
};

const handlePhoneInputChange = (event, setLocalValue) => {
  const { value } = event.target;
  let digitsOnly = value.replace(/\D/g, ""); // Remove non-numeric characters

  // If the number of digits is greater than 10, slice to 10 digits
  if (digitsOnly.length > 10) {
    digitsOnly = digitsOnly.slice(0, 10);
  }

  let formattedPhoneNumber = "";

  // Add dashes after 3rd and 6th digits
  for (let i = 0; i < digitsOnly.length; i++) {
    if (i === 3 || i === 6) {
      formattedPhoneNumber += "-";
    }
    formattedPhoneNumber += digitsOnly[i];
  }
  if (setLocalValue) {
    setLocalValue(formattedPhoneNumber);
  } else {
    return formattedPhoneNumber;
  }
};

const getFieldRow = (field) =>
  field?.row || field?.options?.find((opt) => opt?.row)?.row;

const getRowPrefix = (row) => row?.split("_")[0];

const getFieldOrder = (field) => field?.order ?? field?.options?.[0]?.order;

const getFieldKey = (field) =>
  String(
    isGenericTablePreviewField(field)
      ? (field?.field_instance_id ?? field?.id ?? field?.name ?? "")
      : (field?.field_id ?? field?.name ?? field?.order ?? ""),
  );

const getFieldSortId = (field) => `field:${getFieldKey(field)}`;

const getSectionSortId = (section) => `section:${String(section ?? "")}`;

const GENERIC_TABLE_SORT_ID = "section:__generic-table__";

const getGenericTableKey = (field) => {
  const table = field?.generic_table;
  return String(
    table?.table_id ?? table?.table_label ?? table?.table_name ?? "table",
  );
};

const getGenericTableSortId = (key) => `${GENERIC_TABLE_SORT_ID}:${key}`;

const OPTION_SORT_ID_PREFIX = "option:";
const getOptionSortId = (option) => `${OPTION_SORT_ID_PREFIX}${option?.value}`;
const isOptionSortId = (id) =>
  typeof id === "string" && id.startsWith(OPTION_SORT_ID_PREFIX);
const getOptionValueFromSortId = (id) =>
  String(id).slice(OPTION_SORT_ID_PREFIX.length);

const dedupeRenderableFields = (renderableFields = []) => {
  const seen = new Set();

  return renderableFields.filter((field) => {
    const key = getFieldKey(field);

    if (!key || seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

const dedupeOptionsByValue = (options = []) => {
  const seen = new Set();
  return options.filter((option) => {
    const value = option?.value;
    if (value === undefined || value === null || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const getFieldInstanceKey = (field) =>
  String(field?.field_instance_id ?? field?.name ?? field?.field_id ?? "");

const getParentRules = (field) => {
  if (!field?.parents) return [];
  return Array.isArray(field.parents) ? field.parents : [field.parents];
};

const isMatchingParentRule = (rule, parentField) => {
  if (!rule || !parentField) return false;

  if (rule?.field_instance_id && parentField?.field_instance_id) {
    return rule.field_instance_id === parentField.field_instance_id;
  }

  return Boolean(rule?.name && rule.name === parentField?.name);
};

const getOptionValue = (value) => (isPlainObject(value) ? value.value : value);

const getRuleOptionValues = (rule) =>
  (Array.isArray(rule?.value) ? rule.value : [rule?.value]).map(getOptionValue);

// Use the same selection for the control, its conditions, and the child editor.
const getPreviewSelectedValues = (field, docFields = []) => {
  const optionIds = (field?.options || []).map((option) => option.value);
  const selectedWidgetIds = (docFields || [])
    .filter((widget) => optionIds.includes(widget?.field_id) && widget.value)
    .map((widget) => widget.field_id);
  const currentValues = (
    Array.isArray(field?.value) ? field.value : [field?.value]
  )
    .map(getOptionValue)
    .filter((value) => optionIds.includes(value));
  const defaultValues = (
    Array.isArray(field?.def_value) ? field.def_value : [field?.def_value]
  ).filter((value) => optionIds.includes(value));
  const selectedValues = selectedWidgetIds.length
    ? selectedWidgetIds
    : currentValues.length || Array.isArray(field?.value)
      ? currentValues
      : defaultValues;

  return (field?.type ?? field?.field_type) === "selectGroup" && field?.multiple
    ? selectedValues
    : selectedValues.slice(0, 1);
};

// Stable dependency ordering; saved order values and unrelated items are retained.
const orderPreviewParentsFirst = (items, getFields) => {
  const ordered = [];
  const visited = new Set();
  const visiting = new Set();

  const visit = (item) => {
    if (visited.has(item) || visiting.has(item)) return;
    visiting.add(item);

    getFields(item).forEach((field) => {
      getParentRules(field).forEach((rule) => {
        const parent = items.find((candidate) =>
          getFields(candidate).some((parentField) =>
            isMatchingParentRule(rule, parentField),
          ),
        );
        if (parent && parent !== item) visit(parent);
      });
    });

    visiting.delete(item);
    visited.add(item);
    ordered.push(item);
  };

  items.forEach(visit);
  return ordered;
};

const updateFieldParents = (fields = [], targetField, nextParents = []) => {
  const targetInstanceId = targetField?.field_instance_id;
  const targetName = targetField?.name;
  let changed = false;

  const updatedFields = fields.map((field) => {
    const isTarget = targetInstanceId
      ? field?.field_instance_id === targetInstanceId
      : Boolean(targetName && field?.name === targetName);

    if (!isTarget) return field;

    changed = true;

    return { ...field, parents: nextParents };
  });

  return changed ? updatedFields : fields;
};

const updatePreviewGroupDefault = (
  fields = [],
  targetField,
  defaultOptionIds = [],
) => {
  const linkedFieldIds = getRenderedFieldRawIds(targetField);
  const normalizedDefaults = [...new Set(defaultOptionIds.filter(Boolean))];
  let changed = false;

  const updatedFields = fields.map((field) => {
    if (!linkedFieldIds.has(field?.field_id)) return field;

    changed = true;
    return {
      ...field,
      def_value: normalizedDefaults,
    };
  });

  return changed ? updatedFields : fields;
};

const getRenderedFieldRawIds = (field) => {
  const ids = new Set();

  if (field?.field_id) ids.add(field.field_id);

  field?.options?.forEach((option) => {
    if (option?.value) ids.add(option.value);
  });

  return ids;
};

const ParentChildren = memo(
  ({ field, allFormField = [], onParentsChange, docFields = [] }) => {
    const [addingChild, setAddingChild] = useState(false);
    const [editingChildKey, setEditingChildKey] = useState(null);
    const [draftValues, setDraftValues] = useState([]);
    const [pendingChild, setPendingChild] = useState(null);

    const parentKey = getFieldInstanceKey(field);
    const parentOptions = dedupeOptionsByValue(field?.options || []);

    const children = allFormField.filter((candidate) =>
      getParentRules(candidate).some((rule) =>
        isMatchingParentRule(rule, field),
      ),
    );

    // Prevent linking an ancestor as a child and creating a cycle.
    const wouldCreateCycle = (candidate) => {
      const visited = new Set();

      const visit = (current) => {
        if (getFieldInstanceKey(current) === getFieldInstanceKey(candidate)) {
          return true;
        }

        const key = getFieldInstanceKey(current);
        if (visited.has(key)) return false;
        visited.add(key);

        return getParentRules(current).some((rule) =>
          allFormField.some(
            (possibleParent) =>
              isMatchingParentRule(rule, possibleParent) &&
              visit(possibleParent),
          ),
        );
      };

      return visit(field);
    };

    const availableChildren = allFormField.filter(
      (candidate) =>
        getFieldInstanceKey(candidate) !== parentKey &&
        !isConnectionSourceField(candidate) &&
        !children.some(
          (child) =>
            getFieldInstanceKey(child) === getFieldInstanceKey(candidate),
        ) &&
        !wouldCreateCycle(candidate),
    );

    const removeChild = (child) => {
      onParentsChange(
        child,
        getParentRules(child).filter(
          (rule) => !isMatchingParentRule(rule, field),
        ),
      );
    };

    const addChild = (child) => {
      if (!child) return;

      const currentValue = getPreviewSelectedValues(field, docFields);

      setDraftValues(
        parentOptions.filter((option) => currentValue.includes(option.value)),
      );
      setPendingChild(child);
      setEditingChildKey(getFieldInstanceKey(child));
      setAddingChild(false);
    };

    const cancelEditing = () => {
      setEditingChildKey(null);
      setPendingChild(null);
      setDraftValues([]);
    };

    const saveChild = (child) => {
      const existingParents = getParentRules(child);
      const hasParent = existingParents.some((rule) =>
        isMatchingParentRule(rule, field),
      );

      const nextParents = hasParent
        ? existingParents.map((rule) =>
            isMatchingParentRule(rule, field)
              ? { ...rule, value: draftValues, options: parentOptions }
              : rule,
          )
        : [
            ...existingParents,
            {
              field_instance_id: field?.field_instance_id,
              field_id: field?.field_id,
              name: field?.name,
              field_label: deriveLabel(field),
              value: draftValues,
              options: parentOptions,
            },
          ];

      onParentsChange(child, nextParents);
      cancelEditing();
    };

    const displayedChildren = pendingChild
      ? [...children, pendingChild]
      : children;
    const childGroups = parentOptions.map((option) => ({
      key: option.value,
      label: option.label,
      children: displayedChildren.filter((child) =>
        getParentRules(child).some(
          (rule) =>
            isMatchingParentRule(rule, field) &&
            getRuleOptionValues(rule).includes(option.value),
        ),
      ),
    }));
    const unassignedChildren = displayedChildren.filter(
      (child) => !childGroups.some((group) => group.children.includes(child)),
    );
    if (unassignedChildren.length) {
      childGroups.push({
        key: "unassigned",
        label: "No option selected",
        children: unassignedChildren,
      });
    }

    return (
      <Box
        data-preview-label-editor="true"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        sx={{ mt: 0.5 }}
      >
        {children.length > 0 && (
          <Box sx={{ position: "absolute", top: 4, right: 8, zIndex: 1 }}>
            <Chip
              label={`Parent · ${children.length}`}
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: 11 }}
            />
          </Box>
        )}

        <Stack
          spacing={0.5}
          sx={{
            ml: 1,
            pl: 1.25,
            borderLeft: "2px solid",
            borderColor: children.length ? "primary.main" : "divider",
          }}
        >
          {childGroups
            .filter((group) => group.children.length)
            .map((group) => (
              <Stack key={group.key} spacing={0.5}>
                <Typography variant="caption" fontWeight={600}>
                  {group.label}
                </Typography>
                {group.children.map((child) => {
                  const childKey = getFieldInstanceKey(child);
                  const editing = editingChildKey === childKey;
                  const rule = getParentRules(child).find((item) =>
                    isMatchingParentRule(item, field),
                  );
                  const selectedValues = parentOptions.filter((option) =>
                    getRuleOptionValues(rule).includes(option.value),
                  );

                  return (
                    <Box
                      key={childKey}
                      sx={{
                        border: "1px solid",
                        borderColor: editing ? "primary.main" : "divider",
                        borderRadius: 1,
                        px: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Button
                          size="small"
                          color="inherit"
                          onClick={() => {
                            if (editing) {
                              cancelEditing();
                              return;
                            }

                            setPendingChild(null);
                            setDraftValues(selectedValues);
                            setEditingChildKey(childKey);
                          }}
                          aria-expanded={editing}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            justifyContent: "flex-start",
                            textTransform: "none",
                            px: 0,
                            textAlign: "left",
                          }}
                        >
                          <Typography variant="body2" noWrap>
                            {deriveLabel(child)}
                          </Typography>
                        </Button>

                        <IconButton
                          size="small"
                          aria-label={`Unlink ${deriveLabel(child)}`}
                          onClick={() => {
                            if (
                              getFieldInstanceKey(pendingChild) === childKey
                            ) {
                              cancelEditing();
                              return;
                            }

                            removeChild(child);
                            if (editing) cancelEditing();
                          }}
                          sx={{ p: 0.25 }}
                        >
                          <ClearIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>

                      {editing && (
                        <Box sx={{ mt: 0.5, mb: 1 }}>
                          <Autocomplete
                            multiple
                            size="small"
                            disableCloseOnSelect
                            options={parentOptions}
                            value={draftValues}
                            getOptionLabel={(option) =>
                              String(option.label ?? option.value ?? "")
                            }
                            isOptionEqualToValue={(option, selected) =>
                              option.value === selected.value
                            }
                            onChange={(_, values) => setDraftValues(values)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Show child when parent is"
                              />
                            )}
                          />

                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ mt: 0.75 }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => saveChild(child)}
                              sx={{
                                minWidth: 48,
                                py: 0,
                                px: 1,
                                fontSize: 11,
                                textTransform: "none",
                              }}
                            >
                              Save
                            </Button>

                            <Button
                              size="small"
                              onClick={cancelEditing}
                              sx={{
                                py: 0,
                                fontSize: 11,
                                textTransform: "none",
                              }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            ))}

          {addingChild ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Autocomplete
                fullWidth
                openOnFocus
                size="small"
                options={availableChildren}
                value={null}
                onChange={(_, child) => addChild(child)}
                getOptionLabel={(option) => deriveLabel(option)}
                isOptionEqualToValue={(option, selected) =>
                  getFieldInstanceKey(option) === getFieldInstanceKey(selected)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    placeholder="Select child field"
                  />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 180,
                    overflowY: "auto",
                  },
                }}
              />
              <IconButton
                size="small"
                aria-label="Cancel adding child"
                onClick={() => setAddingChild(false)}
              >
                <ClearIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {children.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  disabled={!availableChildren.length}
                  onClick={() => setAddingChild(true)}
                  sx={{
                    minHeight: 24,
                    py: 0,
                    px: 1,
                    textTransform: "none",
                    fontSize: 12,
                  }}
                >
                  Add child
                </Button>
              )}

              {children.length > 0 && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    children.forEach(removeChild);
                    setEditingChildKey(null);
                  }}
                  sx={{ py: 0, textTransform: "none", fontSize: 12 }}
                >
                  Remove all children
                </Button>
              )}
            </Box>
          )}
        </Stack>
      </Box>
    );
  },
);

const GroupDefaultEditor = memo(
  ({ field, docFields = [], onDefaultChange }) => {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const options = dedupeOptionsByValue(field?.options);
    const optionIds = new Set(options.map((option) => option.value));
    const linkedFieldIds = getRenderedFieldRawIds(field);
    const widgetDefault = docFields.find(
      (widget) =>
        linkedFieldIds.has(widget?.field_id) &&
        Array.isArray(widget?.def_value),
    )?.def_value;
    const storedDefaults = Array.isArray(widgetDefault)
      ? widgetDefault
      : Array.isArray(field?.def_value)
        ? field.def_value
        : field?.def_value
          ? [field.def_value]
          : [];
    const selectedDefaults = options.filter((option) =>
      storedDefaults.includes(option.value),
    );
    const allowMultiple =
      field?.field_type === "selectGroup" && field?.multiple;
    const [draftDefaults, setDraftDefaults] = useState(selectedDefaults);

    useEffect(() => {
      if (!editing) setDraftDefaults(selectedDefaults);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing, field?.def_value, widgetDefault]);

    const persistDefault = async (values) => {
      if (saving) return;

      try {
        setSaving(true);
        setError("");
        await onDefaultChange(field, values);
        setDraftDefaults(
          options.filter((option) => values.includes(option.value)),
        );
        setEditing(false);
      } catch (saveError) {
        setError(
          saveError?.response?.data?.message ||
            saveError?.message ||
            "Unable to save the default value.",
        );
      } finally {
        setSaving(false);
      }
    };

    const clearDefault = () => persistDefault([]);

    const saveDefault = async () => {
      const values = (allowMultiple ? draftDefaults : draftDefaults.slice(0, 1))
        .map((option) => option?.value)
        .filter((value) => optionIds.has(value));

      await persistDefault(values);
    };

    if (!storedDefaults.length) {
      return null;
    }

    return (
      <Box
        data-preview-label-editor="true"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        sx={{ mt: 0.75 }}
      >
        {editing ? (
          <Stack spacing={0.75}>
            <Autocomplete
              multiple={Boolean(allowMultiple)}
              size="small"
              options={options}
              value={allowMultiple ? draftDefaults : (draftDefaults[0] ?? null)}
              getOptionLabel={(option) =>
                String(
                  option?.label ?? option?.field_label ?? option?.value ?? "",
                )
              }
              isOptionEqualToValue={(option, selected) =>
                option.value === selected.value
              }
              onChange={(_, selection) =>
                setDraftDefaults(
                  allowMultiple ? selection : selection ? [selection] : [],
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Default value"
                  placeholder={draftDefaults.length ? "" : "Select an option"}
                />
              )}
            />
            <Stack direction="row" spacing={0.5}>
              <Button
                size="small"
                variant="contained"
                disabled={!draftDefaults.length || saving}
                onClick={saveDefault}
                sx={{ py: 0, textTransform: "none", fontSize: 11 }}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setDraftDefaults(selectedDefaults);
                  setError("");
                  setEditing(false);
                }}
                disabled={saving}
                sx={{ py: 0, textTransform: "none", fontSize: 11 }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : selectedDefaults.length ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`Default: ${selectedDefaults
                .map(
                  (option) =>
                    option.label ?? option.field_label ?? option.value,
                )
                .join(", ")}`}
              onClick={() => setEditing(true)}
              sx={{ maxWidth: "calc(100% - 34px)" }}
            />
            <IconButton
              size="small"
              aria-label="Clear default value"
              onClick={clearDefault}
              disabled={saving}
              sx={{ p: 0.25 }}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        ) : (
          <Button
            size="small"
            variant="outlined"
            disabled={!options.length}
            onClick={() => {
              setError("");
              setEditing(true);
            }}
            sx={{
              minHeight: 24,
              py: 0,
              px: 1,
              textTransform: "none",
              fontSize: 12,
            }}
          >
            Set default
          </Button>
        )}
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 0.5 }}
          >
            {error}
          </Typography>
        )}
      </Box>
    );
  },
);

const buildRowUnits = (sortedFields) => {
  const units = [];
  const unitByPrefix = new Map();

  sortedFields.forEach((field) => {
    const row = getFieldRow(field);
    const sharesRowWithParent =
      row &&
      getParentRules(field).some((rule) =>
        sortedFields.some(
          (parent) =>
            isMatchingParentRule(rule, parent) &&
            getRowPrefix(getFieldRow(parent)) === getRowPrefix(row),
        ),
      );

    if (!row || sharesRowWithParent) {
      units.push({
        id: `unit:${getFieldKey(field)}`,
        type: "single",
        field,
        fields: [field],
      });
      return;
    }

    const prefix = getRowPrefix(row);
    const existingUnit = unitByPrefix.get(prefix);

    if (existingUnit) {
      existingUnit.fields.push(field);
      return;
    }

    const unit = {
      id: `row:${prefix}:${getFieldKey(field)}`,
      type: "row",
      field,
      fields: [field],
    };
    unitByPrefix.set(prefix, unit);
    units.push(unit);
  });

  return orderPreviewParentsFirst(units, (unit) => unit.fields);
};

const copyPreviewFieldDetail = async (value) => {
  const text = String(value ?? "");

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  textArea.remove();
};

const FieldDetailsControl = memo(({ field, compact = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [copyStatus, setCopyStatus] = useState("");
  const details = [
    { key: "name", label: "Field name", value: field?.name },
    { key: "id", label: "Field ID", value: field?.field_id },
    {
      key: "instance id",
      label: "Field Instance ID",
      value: field?.field_instance_id,
    },
  ];

  const handleCopy = async (detail) => {
    try {
      await copyPreviewFieldDetail(detail.value);
      setCopyStatus(`${detail.label} copied`);
    } catch (error) {
      setCopyStatus(`Unable to copy ${detail.label.toLowerCase()}`);
    }
  };

  return (
    <Box
      data-preview-label-editor="true"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      sx={
        compact
          ? { flexShrink: 0 }
          : { display: "flex", justifyContent: "flex-end" }
      }
    >
      <Tooltip title="View and copy field name or ID">
        {compact ? (
          <IconButton
            size="small"
            aria-label="View field details"
            color="primary"
            onClick={(event) => {
              setCopyStatus("");
              setAnchorEl(event.currentTarget);
            }}
            sx={{ p: 0.25 }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : (
          <Button
            size="small"
            startIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={(event) => {
              setCopyStatus("");
              setAnchorEl(event.currentTarget);
            }}
            sx={{
              minHeight: 22,
              py: 0,
              px: 0.5,
              textTransform: "none",
              fontSize: 11,
            }}
          >
            Field details
          </Button>
        )}
      </Tooltip>
      <Popover
        data-preview-label-editor="true"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setCopyStatus("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Stack spacing={1} sx={{ p: 1.5, width: 360, maxWidth: "80vw" }}>
          {details.map((detail) => (
            <Box key={detail.key}>
              <Typography variant="caption" color="text.secondary">
                {detail.label}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{ flex: 1, overflowWrap: "anywhere", userSelect: "text" }}
                >
                  {String(detail.value || "Not assigned")}
                </Typography>
                <Tooltip title={`Copy ${detail.label.toLowerCase()}`}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Copy ${detail.label.toLowerCase()}`}
                      disabled={!detail.value}
                      onClick={() => handleCopy(detail)}
                      sx={{ p: 0.25 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          ))}
          {copyStatus && (
            <Typography
              variant="caption"
              color={copyStatus.startsWith("Unable") ? "error" : "success.main"}
            >
              {copyStatus}
            </Typography>
          )}
        </Stack>
      </Popover>
    </Box>
  );
});

const EditableTypographyLabel = memo(
  ({ field, label, onLabelCommit, locateFields, typographyProps = {} }) => {
    const [isEditingLabel, setIsEditingLabel] = useState(false);
    const [localLabel, setLocalLabel] = useState(label);
    const labelEditorRef = useRef(null);
    const labelDraftRef = useRef(label);
    const isEditingLabelRef = useRef(false);

    const commitLabel = useCallback(() => {
      if (!isEditingLabelRef.current) return;

      const nextLabel = labelDraftRef.current;
      isEditingLabelRef.current = false;
      labelEditorRef.current?.blur();
      onLabelCommit?.(field, nextLabel);
      setLocalLabel(nextLabel.trim() || label);
      setIsEditingLabel(false);
    }, [field, label, onLabelCommit]);

    useEffect(() => {
      if (!isEditingLabel) return undefined;

      const id = requestAnimationFrame(() => {
        const el = labelEditorRef.current;
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      });

      return () => cancelAnimationFrame(id);
    }, [isEditingLabel]);

    useEffect(() => {
      if (!isEditingLabel) return undefined;

      const handleClickAway = (event) => {
        if (labelEditorRef.current?.contains(event.target)) return;
        commitLabel();
      };

      document.addEventListener("pointerdown", handleClickAway, true);
      return () =>
        document.removeEventListener("pointerdown", handleClickAway, true);
    }, [commitLabel, isEditingLabel]);

    const startEditingLabel = (event) => {
      if (!locateFields || !onLabelCommit || isEditingLabel) return;

      event.preventDefault();
      event.stopPropagation();
      labelDraftRef.current = label;
      isEditingLabelRef.current = true;
      setLocalLabel(label);
      setIsEditingLabel(true);
    };

    if (isEditingLabel) {
      return (
        <TextField
          inputRef={labelEditorRef}
          label={isEditingLabel && "Field label"}
          inputProps={{ "data-preview-label-editor": "true" }}
          variant="filled"
          fullWidth
          size="small"
          value={localLabel}
          autoFocus
          onChange={(e) => {
            labelDraftRef.current = e.target.value;
            setLocalLabel(e.target.value);
          }}
          onBlur={commitLabel}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitLabel();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              isEditingLabelRef.current = false;
              setIsEditingLabel(false);
            }
          }}
        />
      );
    }

    return (
      <Typography {...typographyProps} onDoubleClick={startEditingLabel}>
        {label}
      </Typography>
    );
  },
);

export const LocalTextField = memo(
  ({
    field,
    onCommit,
    onLabelCommit,
    demoVersion,
    locateFields,
    enableFieldDetial,
  }) => {
    const label = deriveLabel(field);
    const [localValue, setLocalValue] = useState(field.value || "");
    const [localLabel, setLocalLabel] = useState(label);
    const [isEditingLabel, setIsEditingLabel] = useState(false);
    const prevDemoRef = useRef(demoVersion);
    const labelEditorRef = useRef(null);
    const labelDraftRef = useRef(label);
    const isEditingLabelRef = useRef(false);
    const isMultiline = field.type === "multiline";
    const isValueEntryDisabled = locateFields && !isEditingLabel;

    useEffect(() => {
      if (demoVersion !== prevDemoRef.current || field.value !== localValue) {
        setLocalValue(field.value || "");
        prevDemoRef.current = demoVersion;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value, demoVersion]);

    const isLong =
      (typeof localValue === "string" && localValue.length > 80) || isMultiline;

    const commitLabel = useCallback(() => {
      if (!isEditingLabelRef.current) return;

      const nextLabel = labelDraftRef.current;
      isEditingLabelRef.current = false;
      labelEditorRef.current?.blur();
      onLabelCommit?.(field, nextLabel);
      setLocalLabel(nextLabel.trim() || label);
      setIsEditingLabel(false);
    }, [field, label, onLabelCommit]);

    useEffect(() => {
      if (!isEditingLabel) return undefined;

      const id = requestAnimationFrame(() => {
        const el = labelEditorRef.current;
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      });

      return () => cancelAnimationFrame(id);
    }, [isEditingLabel]);

    useEffect(() => {
      if (!isEditingLabel) return undefined;

      const handleClickAway = (event) => {
        if (labelEditorRef.current?.contains(event.target)) return;
        commitLabel();
      };

      document.addEventListener("pointerdown", handleClickAway, true);
      return () =>
        document.removeEventListener("pointerdown", handleClickAway, true);
    }, [commitLabel, isEditingLabel]);

    return (
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <TextField
          inputRef={labelEditorRef}
          inputProps={
            isEditingLabel
              ? { "data-preview-label-editor": "true" }
              : { readOnly: isValueEntryDisabled }
          }
          variant="filled"
          fullWidth
          margin="normal"
          label={isEditingLabel ? "Field label" : label}
          value={
            isEditingLabel
              ? localLabel
              : field.type === "dollar" || field.type === "money"
                ? (localValue ?? "")
                : localValue || ""
          }
          multiline={!isEditingLabel && isLong}
          type={!isEditingLabel && field.type === "number" ? "number" : "text"}
          autoFocus={isEditingLabel}
          onDoubleClick={(event) => {
            if (!locateFields || isEditingLabel) return;

            event.preventDefault();
            event.stopPropagation();
            labelDraftRef.current = label;
            isEditingLabelRef.current = true;
            setLocalLabel(label);
            setIsEditingLabel(true);
          }}
          onChange={(e) => {
            if (isEditingLabel) {
              labelDraftRef.current = e.target.value;
              setLocalLabel(e.target.value);
            } else if (!locateFields) {
              setLocalValue(e.target.value);
            }
          }}
          onBlur={(event) => {
            if (isEditingLabel) {
              commitLabel();
              return;
            }

            if (locateFields) return;

            if (
              field.type === "dollar" ||
              field.type === "money" ||
              field.type === "total"
            ) {
              let { value } = event.target;
              const formattedValue = formatMoneyValue(value);
              onCommit(
                field?.name,
                field.type === "money"
                  ? formattedValue.replace(/^\$/, "")
                  : formattedValue,
              );
            } else {
              onCommit(field?.name, localValue);
            }
          }}
          onKeyDown={(e) => {
            if (isEditingLabel && e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
              return;
            }

            if (
              !field.text_connection_source &&
              field.value?.length > 310 &&
              e.key !== "Backspace" &&
              e.key !== "Delete" &&
              isMultiline
            )
              e.preventDefault();
          }}
          onKeyUp={(e) => {
            if (locateFields || isEditingLabel) return;

            if (field.type === "mobile") {
              handlePhoneInputChange(e, setLocalValue);
            } else if (field.type === "dollar" || field.type === "money") {
              handleMoneyChange(e, setLocalValue);
            } else if (field.type === "date" || field.type === "todayDate") {
              handleDateChange(e, setLocalValue);
            }
          }}
          rows={4}
          error={
            !isEditingLabel &&
            Boolean(field.text_connection_source && field.text_connection_error)
          }
          helperText={
            !isEditingLabel && field.text_connection_source
              ? field.text_connection_error ||
                "Text continues into the connected widgets when needed."
              : undefined
          }
        />
        {enableFieldDetial && !isEditingLabel && (
          <FieldDetailsControl field={field} />
        )}
      </Box>
    );
  },
);

const SortableRadioOption = memo(
  ({
    option,
    enableSorting,
    locateFields,
    onLabelCommit,
    enableFieldDetial,
  }) => {
    const label = option.label || "";
    const [localLabel, setLocalLabel] = useState(label);
    const [isEditingLabel, setIsEditingLabel] = useState(false);
    const labelEditorRef = useRef(null);
    const labelDraftRef = useRef(label);
    const isEditingLabelRef = useRef(false);

    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({
        id: getOptionSortId(option),
        disabled: !enableSorting,
      });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      display: "flex",
      alignItems: "center",
    };

    const commitLabel = useCallback(() => {
      if (!isEditingLabelRef.current) return;

      const nextLabel = labelDraftRef.current;
      isEditingLabelRef.current = false;
      labelEditorRef.current?.blur();
      onLabelCommit?.(option, nextLabel);
      setLocalLabel(nextLabel.trim() || label);
      setIsEditingLabel(false);
    }, [option, label, onLabelCommit]);

    useEffect(() => {
      if (!isEditingLabel) return undefined;

      const handleClickAway = (event) => {
        if (labelEditorRef.current?.contains(event.target)) return;
        commitLabel();
      };

      document.addEventListener("pointerdown", handleClickAway, true);
      return () =>
        document.removeEventListener("pointerdown", handleClickAway, true);
    }, [commitLabel, isEditingLabel]);

    const startEditingLabel = (event) => {
      if (!locateFields || isEditingLabel) return;

      event.preventDefault();
      event.stopPropagation();
      labelDraftRef.current = label;
      isEditingLabelRef.current = true;
      setLocalLabel(label);
      setIsEditingLabel(true);
    };

    return (
      <Box
        ref={setNodeRef}
        style={style}
        sx={{
          minWidth: 0,
          maxWidth: "100%",
          flex: enableFieldDetial
            ? "1 1 100%"
            : { xs: "0 1 auto", md: "0 1 160px" },
        }}
      >
        {enableSorting && (
          <DragIndicatorIcon
            {...attributes}
            {...listeners}
            sx={{
              fontSize: "1rem",
              fill: "#ababab",
              cursor: "grab",
              mr: 1,
              flexShrink: 0,
            }}
          />
        )}

        {isEditingLabel ? (
          <TextField
            inputRef={labelEditorRef}
            inputProps={{ "data-preview-label-editor": "true" }}
            variant="filled"
            size="small"
            fullWidth
            label="Option label"
            value={localLabel}
            autoFocus
            onChange={(e) => {
              labelDraftRef.current = e.target.value;
              setLocalLabel(e.target.value);
            }}
            onBlur={commitLabel}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitLabel();
              }
            }}
          />
        ) : (
          <>
            <FormControlLabel
              control={<Radio />}
              onDoubleClick={startEditingLabel}
              label={label}
              value={option.value}
              sx={{
                m: 0,
                minWidth: 0,
                maxWidth: "100%",
                flex: 1,
                "& .MuiFormControlLabel-label": {
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                },
              }}
            />
            {enableFieldDetial && (
              <FieldDetailsControl field={option} compact />
            )}
          </>
        )}
      </Box>
    );
  },
);

const OptionReorderContext = createContext(null);
const REORDER_OPTIONS_ACTION = "__preview_reorder_options__";

const ReorderOptionRow = ({ option }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.value });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        mb: 0.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        position: "relative",
        zIndex: isDragging ? 10 : 0,
        boxShadow: isDragging ? 3 : "none",
        bgcolor: "background.paper",
      }}
    >
      <IconButton
        size="small"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${option.label}`}
        sx={{ cursor: "grab", touchAction: "none" }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>

      <Typography
        variant="body2"
        sx={{ minWidth: 0, overflowWrap: "anywhere" }}
      >
        {option.label}
      </Typography>
    </Box>
  );
};

const restrictOptionDrag = ({
  transform,
  draggingNodeRect,
  scrollableAncestorRects,
}) => {
  const container = scrollableAncestorRects?.[0];

  if (!draggingNodeRect || !container) {
    return { ...transform, x: 0 };
  }

  const minY = container.top - draggingNodeRect.top;
  const maxY = container.bottom - draggingNodeRect.bottom;

  return {
    ...transform,
    x: 0,
    y: Math.min(Math.max(transform.y, minY), Math.max(minY, maxY)),
  };
};

const ReorderableSelect = ({
  field,
  enableSorting,
  children,
  onChange,
  ...selectProps
}) => {
  const applyOptionOrder = useContext(OptionReorderContext);
  const selectRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [draftOptions, setDraftOptions] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const canReorder = enableSorting && Boolean(applyOptionOrder);

  useEffect(() => {
    if (!enableSorting) setReorderOpen(false);
  }, [enableSorting]);

  const handleChange = (event, child) => {
    const nextValue = event.target.value;
    const isReorderAction = Array.isArray(nextValue)
      ? nextValue.includes(REORDER_OPTIONS_ACTION)
      : nextValue === REORDER_OPTIONS_ACTION;

    if (isReorderAction) {
      if (!canReorder) return;

      setDraftOptions([...dedupeOptionsByValue(field.options || [])]);
      setMenuOpen(false);
      setReorderOpen(true);
      return;
    }

    onChange?.(event, child);
  };

  return (
    <>
      <Select
        {...selectProps}
        ref={selectRef}
        open={menuOpen}
        onOpen={() => setMenuOpen(true)}
        onClose={() => setMenuOpen(false)}
        onChange={handleChange}
      >
        {canReorder && (
          <MenuItem
            value={REORDER_OPTIONS_ACTION}
            disabled={dedupeOptionsByValue(field.options || []).length < 2}
            sx={{
              gap: 1,
              color: "primary.main",
              borderBottom: "1px solid",
              borderColor: "divider",
              mb: 0.5,
            }}
          >
            <DragIndicatorIcon fontSize="small" />
            Reorder options
          </MenuItem>
        )}

        {children}
      </Select>

      <Popover
        open={reorderOpen && canReorder}
        anchorEl={selectRef.current}
        onClose={() => setReorderOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box
          sx={{ width: 320, maxWidth: "calc(100vw - 32px)", p: 2 }}
          onClick={(event) => event.stopPropagation()}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Reorder options
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Drag the handles to change the order.
          </Typography>

          <DndContext
            sensors={sensors}
            modifiers={[restrictOptionDrag]}
            collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => {
              if (!over || active.id === over.id) return;

              setDraftOptions((previous) => {
                const from = previous.findIndex(
                  (option) => option.value === active.id,
                );
                const to = previous.findIndex(
                  (option) => option.value === over.id,
                );

                return from < 0 || to < 0
                  ? previous
                  : arrayMove(previous, from, to);
              });
            }}
          >
            <SortableContext
              items={draftOptions.map((option) => option.value)}
              strategy={verticalListSortingStrategy}
            >
              <Box
                sx={{
                  maxHeight: 280,
                  overflowY: "auto",
                  overflowX: "hidden",
                  overscrollBehaviorY: "contain",
                }}
              >
                {draftOptions.map((option) => (
                  <ReorderOptionRow key={option.value} option={option} />
                ))}
              </Box>
            </SortableContext>
          </DndContext>

          <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
            <Button onClick={() => setReorderOpen(false)}>Cancel</Button>

            <Button
              variant="contained"
              onClick={() => {
                applyOptionOrder(
                  field,
                  draftOptions.map((option) => option.value),
                );
                setReorderOpen(false);
              }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

const FieldContent = memo(
  ({
    field,
    onChange,
    onLabelCommit,
    demoVersion,
    docFields,
    allFormField,
    fields,
    locateFields,
    enableSorting,
    enableFieldDetial,
    onParentsChange,
    onDefaultChange,
  }) => {
    const fieldNameAndId = `Name: ${field.name} | ID: ${field.field_id}` || "";
    const label = !enableFieldDetial ? deriveLabel(field) : fieldNameAndId;

    const { value, name, multiple } = field;
    const isOptionGroup = ["selectGroup", "checkboxGroup"].includes(
      field?.field_type,
    );
    const visibleGroupOptions = isOptionGroup
      ? dedupeOptionsByValue(field?.options).filter(
          (option) =>
            isVisible(option, allFormField, docFields) && !field.compact,
        )
      : [];

    const currentSession = useSelector(
      (state) => state.sessions.currentSession,
    );
    const currentUser = useSelector((state) => state[currentSession]?.user);
    const designProList = currentUser?.design_professionals;

    // Memoize dependency values based on subscriber field keys.
    // This ensures the effect only runs when relevant form values change.
    const dependency = useMemo(() => {
      const subscribers = field?.formulaFields?.map((f) => f?.field?.name);

      if (!subscribers?.length) {
        return [];
      }

      const allSumFields = allFormField?.filter((field) =>
        subscribers?.includes(field?.name),
      );

      // This creates a dependency list that tracks only specific fields
      return allSumFields.map((field) => field?.value);
    }, [field?.formulaFields, allFormField]);

    useEffect(() => {
      if (!field?.formulaFields?.length) return;

      const valuesByName = Object.fromEntries(
        (allFormField || []).map((formField) => [
          formField?.name,
          formField?.value,
        ]),
      );
      const computedValue = getFormulaUpdate({
        currentValue: value,
        formulaFields: field.formulaFields,
        valuesByName,
      });

      if (computedValue === undefined) return;

      const timer = setTimeout(() => onChange(name, computedValue), 50);

      return () => clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependency);

    if (["PDFRectangle", "PDFLine"].includes(field.type)) return null;

    return (
      <Box flex={1} minWidth={0} position="relative">
        {field.type === "designProAuto" ? (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Autocomplete
              freeSolo
              sx={{ width: "100%" }}
              options={designProList}
              value={
                designProList?.find((option) => option.companyName === value) ||
                value ||
                ""
              }
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.companyName
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  onChange={(e) => {
                    onChange(name, e.target.value);
                  }}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {value && value !== "" && (
                            <IconButton
                              onClick={() => {
                                const mappingList = [
                                  {
                                    formikNames: [
                                      "engineer",
                                      "engineer_name",
                                      "engineerName",
                                    ],
                                    keys: [
                                      "engineerPhone",
                                      "engineer_phone",
                                      "engineerEmail",
                                      "engineer_email",
                                      "engineerAddress",
                                    ],
                                  },
                                  {
                                    formikNames: [
                                      "architect",
                                      "architectName",
                                      "architect_name",
                                    ],
                                    keys: [
                                      "architectPhone",
                                      "architect_phone",
                                      "architectEmail",
                                      "architect_email",
                                      "architectAddress",
                                    ],
                                  },
                                ];

                                // Loop through mappingList and clear the matching fields
                                mappingList.forEach((mapping) => {
                                  if (
                                    mapping.formikNames.includes(field.name)
                                  ) {
                                    mapping.keys.forEach((field) => {
                                      onChange(field, "");
                                    });
                                  }
                                });

                                // Finally, clear the main field
                                onChange(name, "");
                              }}
                              size="small"
                              sx={{
                                marginRight: -4,
                                marginTop: -2,
                              }}
                            >
                              <ClearIcon sx={{ fontSize: "20px" }} />
                            </IconButton>
                          )}
                        </Box>
                      ),
                    },
                  }}
                />
              )}
              onChange={(event, selectedOption) => {
                if (selectedOption) {
                  const mappingList = [
                    {
                      formikNames: [
                        "engineer",
                        "engineer_name",
                        "engineerName",
                      ],
                      keys: [
                        {
                          engineerPhone: "phone",
                          engineerEmail: "email",
                          engineer_email: "email",
                          engineer_phone: "phone",
                          engineerAddress: "address",
                        },
                      ],
                    },
                    {
                      formikNames: [
                        "architect",
                        "architectName",
                        "architect_name",
                      ],
                      keys: [
                        {
                          architectPhone: "phone",
                          architect_phone: "phone",
                          architectEmail: "email",
                          architect_email: "email",
                          architectAddress: "address",
                        },
                      ],
                    },
                  ];

                  // Set the selected company's name
                  onChange(name, selectedOption.companyName);

                  // Update only for the matching formikNames in the list
                  mappingList.forEach((mapping) => {
                    if (mapping.formikNames.includes(field.name)) {
                      mapping.keys.forEach((keyObj) => {
                        Object.entries(keyObj).forEach(([key, value]) => {
                          onChange(key, selectedOption[value]);
                        });
                      });
                    }
                  });
                }
              }}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: "150px",
                    overflowY: "auto",
                  },
                },
              }}
            />
          </Box>
        ) : [
            "text",
            "mobile",
            "email",
            "dollar",
            "money",
            "number",
            "date",
            "todayDate",
            "addressGroup",
            "mobileGroup",
            "multiline",
          ].includes(field.type) ? (
          <LocalTextField
            field={field}
            onCommit={onChange}
            onLabelCommit={onLabelCommit}
            demoVersion={demoVersion}
            locateFields={locateFields}
            enableFieldDetial={enableFieldDetial}
          />
        ) : field.type === "checkboxGroup" ? (
          (() => {
            const visibleOptions = visibleGroupOptions;
            return (
              visibleOptions?.length > 0 && (
                <>
                  {(field?.title || enableFieldDetial) && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 0.5,
                        width: "100%",
                        mt: 2,
                      }}
                    >
                      {field?.title && (
                        <Typography sx={{ fontSize: "16px" }} variant="h6">
                          {field.title}
                        </Typography>
                      )}
                      {enableFieldDetial && (
                        <FieldDetailsControl field={field} compact />
                      )}
                    </Box>
                  )}
                  <FormControl component="fieldset" sx={{ width: "100%" }}>
                    {(() => {
                      const optionValues = (visibleOptions || []).map(
                        (o) => o.value,
                      );

                      const safeVal = getPreviewSelectedValues(
                        field,
                        docFields,
                      ).find((selected) => optionValues.includes(selected));

                      return (
                        <RadioGroup
                          row
                          value={safeVal || ""}
                          onChange={(e) => {
                            const value = visibleOptions?.find(
                              (o) => o.value === e.target.value,
                            );
                            onChange(
                              name,
                              value,
                              field.options.map((option) => option.value),
                            );
                          }}
                        >
                          <SortableContext
                            items={visibleOptions.map((option) =>
                              getOptionSortId(option),
                            )}
                            strategy={rectSortingStrategy}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                flexWrap: "wrap",
                                gap: 1,
                                width: "100%",
                                minWidth: 0,
                              }}
                            >
                              {visibleOptions?.map((option) => (
                                <SortableRadioOption
                                  key={option.value}
                                  option={option}
                                  enableSorting={enableSorting}
                                  locateFields={locateFields}
                                  onLabelCommit={onLabelCommit}
                                  enableFieldDetial={enableFieldDetial}
                                />
                              ))}
                            </Box>
                          </SortableContext>
                        </RadioGroup>
                      );
                    })()}
                  </FormControl>
                </>
              )
            );
          })()
        ) : field.type === "selectGroup" ? (
          (() => {
            const visibleOptions = dedupeOptionsByValue(field?.options).filter(
              (option) => isVisible(option, allFormField, docFields),
            );

            if (!visibleOptions.length) return null;

            const optionValues = visibleOptions.map((option) => option.value);
            const selectedIds = getPreviewSelectedValues(
              field,
              docFields,
            ).filter((selected) => optionValues.includes(selected));
            const safeVal = multiple ? selectedIds : (selectedIds[0] ?? "");

            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <FormControl
                    fullWidth
                    margin="normal"
                    variant="filled"
                    sx={{ minWidth: 0 }}
                  >
                    <InputLabel>{getPreviewFieldLabel(field)}</InputLabel>
                    <ReorderableSelect
                      field={field}
                      enableSorting={enableSorting}
                      multiple={multiple}
                      value={safeVal}
                      onChange={(event) => {
                        if (multiple) {
                          onChange(
                            name,
                            event.target.value,
                            optionValues,
                            true,
                          );
                        } else {
                          const selectedOption = visibleOptions.find(
                            (option) => option.value === event.target.value,
                          );
                          onChange(name, selectedOption, optionValues);
                        }
                      }}
                      label={getPreviewFieldLabel(field)}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            marginTop: "1.5px",
                            maxHeight: 200,
                            width: "auto",
                            maxWidth: "100%",
                          },
                        },
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                      }}
                    >
                      {visibleOptions.map((option, index) => (
                        <MenuItem
                          key={option.value + index}
                          value={option.value}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </ReorderableSelect>
                  </FormControl>
                  {enableFieldDetial && (
                    <FieldDetailsControl field={field} compact />
                  )}
                </Box>
              </Box>
            );
          })()
        ) : field.type === "privateProvider" ? (
          <RenderPrivateProvider
            full_title={field?.field_label}
            privateProvider={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "full_title" ? (
          <EditableTypographyLabel
            field={field}
            label={field?.field_label}
            onLabelCommit={onLabelCommit}
            locateFields={locateFields}
            typographyProps={{
              mt: 2,
              mb: 1,
              align: "center",
              variant: "h6",
            }}
          />
        ) : field.type === "full_subTitle" ? (
          <EditableTypographyLabel
            field={field}
            label={field?.field_label}
            onLabelCommit={onLabelCommit}
            locateFields={locateFields}
            typographyProps={{
              mt: 1.5,
              mb: 0,
              align: "center",
              variant: "subtitle1",
            }}
          />
        ) : field.type === "project_contact_details" ? (
          <RenderProjectContact
            full_title={field?.field_label}
            projectContact={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "designProfessional" ? (
          <RenderDesignPro
            full_title={field?.field_label}
            designPro={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "feeSimple" ? (
          <RenderFeeSimple
            full_title={field?.field_label}
            feeSimple={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "bonding" ? (
          <RenderBonding
            full_title={field?.field_label}
            bonding={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "mortgage" ? (
          <RenderMortgage
            full_title={field?.field_label}
            mortgage={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "termite" ? (
          <RenderTermite
            full_title={field?.field_label}
            termite={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "total" ? (
          field?.isEditable ? (
            <LocalTextField
              field={field}
              onCommit={onChange}
              onLabelCommit={onLabelCommit}
              demoVersion={demoVersion}
              locateFields={locateFields}
            />
          ) : (
            <Stack>
              <EditableTypographyLabel
                field={field}
                label={field?.field_label}
                onLabelCommit={onLabelCommit}
                locateFields={locateFields}
                typographyProps={{
                  mt: 1.5,
                  mb: 0,
                  align: "center",
                  color: "primary",
                  fontSize: 16,
                  fontWeight: 700,
                }}
              />
              <Typography mt={0.5} mb={0} align="center" fontWeight={700}>
                {field?.value || "$0.00"}
              </Typography>
            </Stack>
          )
        ) : field.type === "percentageCheckbox" ? (
          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <FormControlLabel
              control={
                <Checkbox
                  name={field?.name}
                  checked={value}
                  value={value}
                  onChange={(e) => {
                    const currentValue = e.target.checked;
                    onChange(name, currentValue);

                    const totalCost = allFormField?.find((f) =>
                      totalFields?.includes(f?.name),
                    );
                    const totalCostNum = parseFloat(
                      parseDollarStrToNumber(totalCost?.value) ?? 0,
                    );

                    allFormField
                      ?.filter(
                        (f) =>
                          ["dollar", "total"].includes(f.type) &&
                          !totalFields?.includes(f?.name),
                      )
                      .forEach((f) => {
                        const fieldNum = parseFloat(
                          parseDollarStrToNumber(f?.value) ?? 0,
                        );
                        const val = currentValue
                          ? formatPercentageValue(
                              (fieldNum / totalCostNum) * 100,
                            )
                          : formatMoneyValue((fieldNum * totalCostNum) / 100);
                        onChange(f?.name, val);
                      });
                  }}
                  slotProps={{
                    input: { "aria-label": "controlled" },
                  }}
                />
              }
              label={label}
              slotProps={{
                typography: { component: "span", variant: "body2" },
              }}
            />
          </Box>
        ) : null}
        {["selectGroup", "checkboxGroup"].includes(field?.field_type) &&
          onParentsChange && (
            <ParentChildren
              field={field}
              allFormField={allFormField}
              onParentsChange={onParentsChange}
              docFields={docFields}
            />
          )}
        {isOptionGroup && visibleGroupOptions.length > 0 && onDefaultChange && (
          <GroupDefaultEditor
            field={field}
            docFields={docFields}
            onDefaultChange={onDefaultChange}
          />
        )}
      </Box>
    );
  },
);

const FieldDragHandle = ({ fieldType, attributes, listeners }) => (
  <DragIndicatorIcon
    {...attributes}
    {...listeners}
    sx={{
      fontSize: "1.2rem",
      fill: "#ababab",
      mt: [
        "privateProvider",
        "project_contact_details",
        "designProfessional",
        "feeSimple",
        "bonding",
        "mortgage",
        "termite",
      ]?.includes(fieldType)
        ? 5.5
        : 0.5,
      cursor: "grab",
    }}
  />
);

const StandaloneField = memo(
  ({
    unitId,
    field,
    onChange,
    onLabelCommit,
    demoVersion,
    docFields,
    allFormField,
    fields,
    enableSorting,
    locateFields,
    isLocated,
    onLocateField,
    isRowSelected,
    onToggleRowField,
    enableFieldDetial,
    onParentsChange,
    onDefaultChange,
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: unitId, disabled: !enableSorting });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const visibleOptions = dedupeOptionsByValue(field?.options)?.filter(
      (option) => isVisible(option, allFormField, docFields) && !field.compact,
    );

    if (["PDFRectangle", "PDFLine"].includes(field.type)) return null;

    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "default",
          "& input, & textarea": {
            cursor: locateFields ? "default" : undefined,
          },
          borderRadius: 1,
          outline: isLocated || isRowSelected ? "2px solid" : "none",
          outlineColor: isRowSelected ? "primary.main" : "secondary.main",
          outlineOffset: 2,
          backgroundColor: isRowSelected
            ? "rgba(25, 118, 210, 0.08)"
            : "transparent",
        }}
        ref={setNodeRef}
        data-preview-field-key={getFieldKey(field)}
        style={style}
        onMouseDownCapture={(event) => {
          if (event.target.closest?.("[data-preview-label-editor]")) return;
          if (!locateFields && !event.ctrlKey && !event.metaKey) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        onClickCapture={(event) => {
          if (event.target.closest?.("[data-preview-label-editor]")) return;
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            event.stopPropagation();
            onToggleRowField?.(field);
            return;
          }
          if (!locateFields) return;
          event.preventDefault();
          event.stopPropagation();
          onLocateField?.(field);
        }}
      >
        {field.type &&
          enableSorting &&
          (!field?.options?.length || visibleOptions?.length > 0) && (
            <FieldDragHandle
              fieldType={field.type}
              attributes={attributes}
              listeners={listeners}
            />
          )}
        <FieldContent
          field={field}
          onChange={onChange}
          onLabelCommit={onLabelCommit}
          demoVersion={demoVersion}
          docFields={docFields}
          allFormField={allFormField}
          fields={fields}
          locateFields={locateFields}
          enableSorting={enableSorting}
          enableFieldDetial={enableFieldDetial}
          onParentsChange={onParentsChange}
          onDefaultChange={onDefaultChange}
        />
      </Box>
    );
  },
);

const RowField = memo(
  ({
    field,
    onChange,
    onLabelCommit,
    demoVersion,
    docFields,
    allFormField,
    fields,
    enableSorting,
    locateFields,
    isLocated,
    onLocateField,
    isRowSelected,
    onToggleRowField,
    enableFieldDetial,
    onParentsChange,
    onDefaultChange,
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: getFieldSortId(field), disabled: !enableSorting });
    const style = { transform: CSS.Transform.toString(transform), transition };

    if (["PDFRectangle", "PDFLine"].includes(field.type)) return null;

    const visibleOptions = dedupeOptionsByValue(field?.options)?.filter(
      (option) => isVisible(option, allFormField, docFields) && !field.compact,
    );

    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "default",
          "& input, & textarea": {
            cursor: locateFields ? "default" : undefined,
          },
          borderRadius: 1,
          outline: isLocated || isRowSelected ? "2px solid" : "none",
          outlineColor: isRowSelected ? "primary.main" : "secondary.main",
          outlineOffset: 2,
          backgroundColor: isRowSelected
            ? "rgba(25, 118, 210, 0.08)"
            : "transparent",
        }}
        ref={setNodeRef}
        data-preview-field-key={getFieldKey(field)}
        style={style}
        onMouseDownCapture={(event) => {
          if (event.target.closest?.("[data-preview-label-editor]")) return;
          if (!locateFields && !event.ctrlKey && !event.metaKey) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        onClickCapture={(event) => {
          if (event.target.closest?.("[data-preview-label-editor]")) return;
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            event.stopPropagation();
            onToggleRowField?.(field);
            return;
          }
          if (!locateFields) return;
          event.preventDefault();
          event.stopPropagation();
          onLocateField?.(field);
        }}
      >
        {enableSorting &&
          (!field?.options?.length || visibleOptions?.length > 0) && (
            <DragIndicatorIcon
              {...attributes}
              {...listeners}
              sx={{
                fontSize: "1rem",
                fill: "#ababab",
                cursor: "grab",
                mt: [
                  "privateProvider",
                  "project_contact_details",
                  "designProfessional",
                  "feeSimple",
                  "bonding",
                  "mortgage",
                  "termite",
                ]?.includes(field?.type)
                  ? 5.5
                  : 0.5,
              }}
            />
          )}
        <FieldContent
          field={field}
          onChange={onChange}
          onLabelCommit={onLabelCommit}
          demoVersion={demoVersion}
          docFields={docFields}
          allFormField={allFormField}
          fields={fields}
          locateFields={locateFields}
          enableSorting={enableSorting}
          enableFieldDetial={enableFieldDetial}
          onParentsChange={onParentsChange}
          onDefaultChange={onDefaultChange}
        />
      </Box>
    );
  },
);
export const isVisible = (field, fields, docFields) => {
  return getParentRules(field).every((parent) => {
    const parentField = fields?.find((candidate) =>
      isMatchingParentRule(parent, candidate),
    );
    if (!parentField) return true;

    const selectedValues = parentField.options?.length
      ? getPreviewSelectedValues(parentField, docFields)
      : [getOptionValue(parentField.value)];
    const formValue = selectedValues[0];
    const matchedFilter = filter_number_input_values?.find(
      (filter) => filter.parentName === parent?.name,
    );

    if (Array.isArray(parent?.value)) {
      return getRuleOptionValues(parent).some((value) =>
        selectedValues.includes(value),
      );
    } else if (matchedFilter) {
      // If it's based on "num_units", check logic to show fields based on selection
      const selectedUnitIndex =
        unitNumMapping[
          String(formValue ?? "").replace(matchedFilter.formValue, "")
        ];
      // Extract numeric value from "unit_one", "unit_two"
      const unitIndex =
        unitNumMapping[parent?.value?.replace(matchedFilter.formValue, "")]; // Extract numeric value for build_ fields

      // Show fields that correspond to <= the selected number of units
      return unitIndex <= selectedUnitIndex;
    } else {
      return selectedValues.includes(getOptionValue(parent.value));
    }
  });
};

const RowContainer = ({
  children,
  rowId,
  fieldIds,
  enableSorting,
  childFields,
  allFormField,
  docFields,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: rowId, disabled: !enableSorting });

  const style = { transform: CSS.Transform.toString(transform), transition };

  const visibleChildFields = childFields.filter((f) => {
    const visibleOptions = f?.options?.filter(
      (option) => isVisible(option, allFormField, docFields) && !f.compact,
    );

    return !f?.options?.length || visibleOptions?.length > 0;
  });

  return (
    <Box
      ref={setNodeRef}
      style={enableSorting ? style : undefined}
      sx={{
        display: "flex",
        width: "100%",
        minWidth: 0,
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      {enableSorting && visibleChildFields?.length > 0 && (
        <DragIndicatorIcon
          {...attributes}
          {...listeners}
          sx={{ fontSize: "1.2rem", fill: "#ababab", mt: 0.5, cursor: "grab" }}
        />
      )}
      <SortableContext
        items={fieldIds ?? []}
        strategy={horizontalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </Box>
  );
};

const RenderUnit = ({
  unit,
  onChange,
  onLabelCommit,
  demoVersion,
  docFields,
  allFormField,
  enableSorting,
  sectionFields,
  locateFields,
  locatedField,
  onLocateField,
  rowSelectedFieldKeys,
  onToggleRowField,
  enableFieldDetial,
  onParentsChange,
  onDefaultChange,
}) => {
  if (unit.type === "row") {
    const fieldIds = unit.fields.map((f) => getFieldSortId(f));
    return (
      <RowContainer
        rowId={unit.id}
        fieldIds={fieldIds}
        enableSorting={enableSorting}
        childFields={unit.fields}
        docFields={docFields}
        allFormField={allFormField}
      >
        {unit.fields.map((fieldInRow) => (
          <RowField
            key={getFieldSortId(fieldInRow)}
            field={fieldInRow}
            onChange={onChange}
            onLabelCommit={onLabelCommit}
            demoVersion={demoVersion}
            docFields={docFields}
            allFormField={allFormField}
            fields={sectionFields}
            enableSorting={enableSorting}
            locateFields={locateFields}
            isLocated={getRenderedFieldRawIds(fieldInRow).has(
              locatedField?.field_id,
            )}
            onLocateField={onLocateField}
            isRowSelected={rowSelectedFieldKeys.has(getFieldKey(fieldInRow))}
            onToggleRowField={onToggleRowField}
            enableFieldDetial={enableFieldDetial}
            onParentsChange={onParentsChange}
            onDefaultChange={onDefaultChange}
          />
        ))}
      </RowContainer>
    );
  }

  return (
    <StandaloneField
      unitId={unit.id}
      field={unit.fields[0]}
      onChange={onChange}
      onLabelCommit={onLabelCommit}
      demoVersion={demoVersion}
      docFields={docFields}
      allFormField={allFormField}
      fields={sectionFields}
      enableSorting={enableSorting}
      locateFields={locateFields}
      isLocated={getRenderedFieldRawIds(unit.fields[0]).has(
        locatedField?.field_id,
      )}
      onLocateField={onLocateField}
      isRowSelected={rowSelectedFieldKeys.has(getFieldKey(unit.fields[0]))}
      onToggleRowField={onToggleRowField}
      enableFieldDetial={enableFieldDetial}
      onParentsChange={onParentsChange}
      onDefaultChange={onDefaultChange}
    />
  );
};

const SectionCard = memo(
  ({
    item,
    onFieldChange,
    onFieldLabelChange,
    demoVersion,
    docFields,
    allFormField,
    handleDragEnd,
    enableSorting,
    sectionDragActive,
    locateFields,
    locatedField,
    onLocateField,
    rowSelectedFieldKeys,
    onToggleRowField,
    enableFieldDetial,
    onParentsChange,
    onDefaultChange,
  }) => {
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );
    const {
      attributes: sectionAttributes,
      listeners: sectionListeners,
      setNodeRef: setSectionNodeRef,
      transform: sectionTransform,
      transition: sectionTransition,
      isDragging: isSectionDragging,
    } = useSortable({
      id: getSectionSortId(item.section),
      disabled: !enableSorting,
    });

    const sectionStyle = {
      transform: CSS.Transform.toString(sectionTransform),
      transition: sectionTransition,
      opacity: isSectionDragging ? 0 : 1,
    };

    const items = item.fields?.filter((field) => !isHiddenPreviewField(field));

    const sortedItems = dedupeRenderableFields(items).sort(
      (a, b) => getFieldOrder(a) - getFieldOrder(b),
    );
    const rowUnits = buildRowUnits(sortedItems);
    const sortableIds = rowUnits.map((u) => u.id);
    return (
      <Box
        ref={setSectionNodeRef}
        style={enableSorting ? sectionStyle : undefined}
        sx={{
          backgroundColor: "#fff",
          border: "2px solid #d3d3d3",
          borderRadius: "15px",
          px: 2,
          pb: 1,
          boxShadow:
            "0px 4px 6px rgba(0, 0, 0, 0.4), 0px 1px 3px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          {enableSorting && (
            <DragIndicatorIcon
              {...sectionAttributes}
              {...sectionListeners}
              sx={{
                position: "absolute",
                left: 0,
                fontSize: "1.4rem",
                fill: "#ababab",
                cursor: "grab",
              }}
            />
          )}
          <Typography variant="h5" align="center" mb={2} mt={1} color="primary">
            {getPreviewSectionTitle(item)}
          </Typography>
        </Box>
        {!sectionDragActive && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableIds ?? []}
              strategy={verticalListSortingStrategy}
            >
              <Grid container spacing={1}>
                {rowUnits.map((unit) => {
                  const visibleFields = unit.fields.filter((f) =>
                    isVisible(f, allFormField, docFields),
                  );
                  if (visibleFields.length === 0) return null;

                  const isCompact =
                    unit.field?.type === "addressGroup" ||
                    unit.field?.type === "mobileGroup";

                  return (
                    <Grid
                      key={`unit-${unit.id}`}
                      size={{
                        xs: 12,
                        sm: 12,
                        md: isCompact ? 4 : 12,
                      }}
                      px={1}
                    >
                      <RenderUnit
                        unit={{ ...unit, fields: visibleFields }}
                        onChange={onFieldChange}
                        onLabelCommit={onFieldLabelChange}
                        demoVersion={demoVersion}
                        docFields={docFields}
                        allFormField={allFormField}
                        enableSorting={enableSorting}
                        sectionFields={item.fields}
                        locateFields={locateFields}
                        locatedField={locatedField}
                        onLocateField={onLocateField}
                        rowSelectedFieldKeys={rowSelectedFieldKeys}
                        onToggleRowField={onToggleRowField}
                        enableFieldDetial={enableFieldDetial}
                        onParentsChange={onParentsChange}
                        onDefaultChange={onDefaultChange}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </SortableContext>
          </DndContext>
        )}
      </Box>
    );
  },
);

const DirectFieldCard = memo(
  ({
    fields,
    onFieldChange,
    onFieldLabelChange,
    demoVersion,
    docFields,
    allFormField,
    handleDragEnd,
    enableSorting,
    locateFields,
    locatedField,
    onLocateField,
    rowSelectedFieldKeys,
    onToggleRowField,
    enableFieldDetial,
    onParentsChange,
    onDefaultChange,
  }) => {
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    if (!fields?.length) return null;

    const items = fields?.filter((field) => !isHiddenPreviewField(field));

    const sortedItems = dedupeRenderableFields(items).sort(
      (a, b) => getFieldOrder(a) - getFieldOrder(b),
    );
    const rowUnits = buildRowUnits(sortedItems);
    const sortableIds = rowUnits.map((u) => u.id);

    return (
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "2px solid #d3d3d3",
          borderRadius: "15px",
          px: 2,
          pb: 1,
          boxShadow:
            "0px 4px 6px rgba(0, 0, 0, 0.4), 0px 1px 3px rgba(0, 0, 0, 0.08)",
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortableIds ?? []}
            strategy={verticalListSortingStrategy}
          >
            <Grid container spacing={1}>
              {rowUnits.map((unit) => {
                const visibleFields = unit.fields.filter((f) =>
                  isVisible(f, allFormField, docFields),
                );
                if (visibleFields.length === 0) return null;

                const isCompact =
                  unit.field?.type === "addressGroup" ||
                  unit.field?.type === "mobileGroup";

                return (
                  <Grid
                    key={`unit-${unit.id}`}
                    size={{
                      xs: 12,
                      sm: 12,
                      md: isCompact ? 4 : 12,
                    }}
                    px={1}
                  >
                    <RenderUnit
                      unit={{ ...unit, fields: visibleFields }}
                      onChange={onFieldChange}
                      onLabelCommit={onFieldLabelChange}
                      demoVersion={demoVersion}
                      docFields={docFields}
                      allFormField={allFormField}
                      enableSorting={enableSorting}
                      sectionFields={fields}
                      locateFields={locateFields}
                      locatedField={locatedField}
                      onLocateField={onLocateField}
                      rowSelectedFieldKeys={rowSelectedFieldKeys}
                      onToggleRowField={onToggleRowField}
                      enableFieldDetial={enableFieldDetial}
                      onParentsChange={onParentsChange}
                      onDefaultChange={onDefaultChange}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </SortableContext>
        </DndContext>
      </Box>
    );
  },
);
const SKIP_TYPES = new Set(["PDFRectangle", "PDFLine"]);
const CANVAS_AREA_FIELD_ID = "801fc1f4-945b-4ac7-8ca7-42f307178039";
const isCanvasAreaField = (field) => field?.field_id === CANVAS_AREA_FIELD_ID;

const PreviewCharacterInput = ({ field, onCommit, disabled, demoVersion }) => {
  const [draft, setDraft] = useState(String(field.value ?? ""));

  useEffect(() => {
    setDraft(String(field.value ?? ""));
  }, [field.name, field.value, demoVersion]);

  return (
    <Box
      onBlur={(event) => {
        // Commit only when focus leaves this input wrapper.
        if (event.currentTarget.contains(event.relatedTarget)) return;
        if (disabled) return;

        if (draft !== String(field.value ?? "")) {
          onCommit(field.name, draft);
        }
      }}
    >
      <ConnectedCharacterInputs
        sources={[field]}
        values={{ [field.name]: draft }}
        disabled={disabled}
        onChange={(_, value) => setDraft(value)}
      />
    </Box>
  );
};

const PreviewForm = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  setDocCurrentProcessingFile,
  fillDemoRef,
  clearRef,
  fields,
  enableSorting,
  locateFields,
  locatedField,
  onLocateField,
  onHoverField,
  rowSelectedFields,
  onToggleRowField,
  scrollToFieldRequest,
  fieldDetails,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [directItems, setDirectItems] = useState([]);
  const [allFormField, setAllFormField] = useState([]);
  const [demoVersion, setDemoVersion] = useState(0);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const scrollContainerRef = useRef(null);
  const rowSelectedFieldKeys = useMemo(
    () => new Set(rowSelectedFields.map((field) => getFieldKey(field))),
    [rowSelectedFields],
  );
  const sectionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const fieldArray = (
      currentProcessingFile?.document?.tempFields?.formFields ?? []
    )
      .filter(isRightPreviewFormField)
      .filter((field) => !isCanvasAreaField(field))
      .filter((field) => !isGenericTablePreviewField(field));

    const { sections, directItems, allFields } = normalizePreviewSections(
      getSections(fieldArray, fields, {
        measureTextConnections: true,
      }),
    );

    setSections(
      sections
        .map((section) => ({
          ...section,
          fields: section.fields.filter(
            (field) => !isConnectionSourceField(field),
          ),
        }))
        .filter((section) => section.fields.length),
    );
    setDirectItems(
      directItems.filter((field) => !isConnectionSourceField(field)),
    );
    setAllFormField(allFields);
    setLoading(false);
  }, [currentProcessingFile?.document?.tempFields?.formFields, fields]);

  useEffect(() => {
    if (!locateFields || !scrollToFieldRequest?.fieldId) return;

    const matchingField = allFormField.find((field) =>
      getRenderedFieldRawIds(field).has(scrollToFieldRequest.fieldId),
    );
    const fieldKey = getFieldKey(matchingField);
    if (!fieldKey) return;

    const frameId = requestAnimationFrame(() => {
      scrollPreviewFieldIntoView(scrollContainerRef.current, fieldKey);
    });

    return () => cancelAnimationFrame(frameId);
  }, [allFormField, locateFields, scrollToFieldRequest]);

  const mapFormFields = useCallback(
    (transform) =>
      setCurrentProcessingFile((previous) => {
        const tempFields = previous?.document?.tempFields;
        if (!tempFields) return previous;

        const transformedFields = (tempFields.formFields || []).map(transform);

        const characterResolvedFields = applyConnectedCharacterValues(
          transformedFields,
          {},
          { strict: false },
        );

        const resolved = applyConnectedTextValues(characterResolvedFields);

        return {
          ...previous,
          document: {
            ...previous.document,
            tempFields: {
              ...tempFields,
              formFields: resolved.fields,
            },
          },
        };
      }),
    [setCurrentProcessingFile],
  );

  const handleFieldChange = useCallback(
    (name, newValue, optionValues, isMultiple) => {
      const textSource = allFormField.find(
        (field) => field.name === name && field.text_connection_source,
      );

      if (textSource) {
        return mapFormFields((field) => {
          if (
            isTextConnectedField(field) &&
            field.text_connection.source_field_id === textSource.field_id
          ) {
            return {
              ...field,
              text_connection: {
                ...field.text_connection,
                source_value: String(newValue ?? ""),
              },
            };
          }

          return field;
        });
      }
      if (
        allFormField.some(
          (field) => field.name === name && isConnectionSourceField(field),
        )
      ) {
        setCurrentProcessingFile((prev) => ({
          ...prev,
          document: {
            ...prev.document,
            tempFields: {
              ...prev.document.tempFields,
              formFields: applyConnectedCharacterValues(
                prev.document.tempFields.formFields,
                { [name]: newValue },
                { strict: false },
              ),
            },
          },
        }));
        return;
      }
      const form_field_id = allFormField?.find(
        (field) => field?.name === name,
      )?.field_id;

      return mapFormFields((f) => {
        // Option widgets must remain booleans, even if their ID also matches
        // the rendered group. Otherwise a truthy option object stays selected.
        if (isMultiple && optionValues?.includes(f.field_id)) {
          return { ...f, value: newValue.includes(f.field_id) };
        }
        if (newValue?.value && optionValues?.includes(f.field_id)) {
          return { ...f, value: f.field_id === newValue.value };
        }

        if (
          f.field_id === form_field_id &&
          !isConnectedCharacterField(f) &&
          !isTextConnectedField(f)
        )
          return { ...f, value: newValue };

        return f;
      });
    },
    [mapFormFields, allFormField, setCurrentProcessingFile],
  );

  const handleGenericTableCellChange = useCallback(
    (changedField, newValue) => {
      const changedInstanceId = changedField?.field_instance_id;
      const changedName = changedField?.name;

      return mapFormFields((field) => {
        const isSameField = changedInstanceId
          ? field?.field_instance_id === changedInstanceId
          : field?.name === changedName;

        return isSameField ? { ...field, value: newValue } : field;
      });
    },
    [mapFormFields],
  );

  const handleGenericTableNameChange = useCallback(
    (tableId, axis, index, nextName) => {
      const normalizedName = String(nextName ?? "").trim();
      if (
        !locateFields ||
        !tableId ||
        (axis !== "row" && axis !== "column") ||
        !Number.isInteger(index) ||
        index < 0 ||
        !normalizedName
      ) {
        return;
      }

      const indexKey = axis === "row" ? "row_index" : "column_index";
      const nameKey = axis === "row" ? "row_name" : "column_name";
      const updateFieldName = (field) => {
        const metadata = field?.generic_table;
        if (
          !isGenericTablePreviewField(field) ||
          metadata.table_id !== tableId ||
          Number(metadata[indexKey]) !== index
        ) {
          return field;
        }

        const nextMetadata = { ...metadata, [nameKey]: normalizedName };
        const nextLabel =
          nextMetadata.header_mode === "named_rows"
            ? [
                nextMetadata.table_label,
                nextMetadata.row_name,
                nextMetadata.column_name,
              ]
                .filter(Boolean)
                .join(" - ")
            : [
                nextMetadata.table_label,
                nextMetadata.column_name,
                Number(nextMetadata.row_index) + 1,
              ].join(" - ");

        if (
          metadata[nameKey] === normalizedName &&
          field.field_label === nextLabel &&
          field.label === nextLabel
        ) {
          return field;
        }

        return {
          ...field,
          field_label: nextLabel,
          label: nextLabel,
          generic_table: nextMetadata,
        };
      };

      const updateTableName = (previous) => {
        const tempFields = previous?.document?.tempFields;
        if (!tempFields) return previous;

        return {
          ...previous,
          document: {
            ...previous.document,
            tempFields: {
              ...tempFields,
              formFields: tempFields.formFields?.map(updateFieldName),
              selectedFormField: updateFieldName(tempFields.selectedFormField),
              selectedExtraFields: Array.isArray(tempFields.selectedExtraFields)
                ? tempFields.selectedExtraFields.map(updateFieldName)
                : tempFields.selectedExtraFields,
            },
          },
        };
      };

      setCurrentProcessingFile(updateTableName);
      setDocCurrentProcessingFile(updateTableName);
    },
    [locateFields, setCurrentProcessingFile, setDocCurrentProcessingFile],
  );

  const handleFieldLabelChange = useCallback(
    (field, nextLabel) => {
      const updateLabel = (prev) => ({
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            formFields: updatePreviewFieldInstanceLabel(
              prev.document.tempFields.formFields,
              field,
              nextLabel,
            ),
          },
        },
      });

      setCurrentProcessingFile(updateLabel);
      setDocCurrentProcessingFile(updateLabel);
    },
    [setCurrentProcessingFile, setDocCurrentProcessingFile],
  );

  const handleFieldParentsChange = useCallback(
    (field, nextParents) => {
      const updateParents = (previous) => {
        const tempFields = previous?.document?.tempFields;
        if (!tempFields) return previous;

        return {
          ...previous,
          document: {
            ...previous.document,
            tempFields: {
              ...tempFields,
              formFields: updateFieldParents(
                tempFields.formFields,
                field,
                nextParents,
              ),
              selectedFormField: tempFields.selectedFormField
                ? updateFieldParents(
                    [tempFields.selectedFormField],
                    field,
                    nextParents,
                  )[0]
                : tempFields.selectedFormField,
              selectedExtraFields: Array.isArray(tempFields.selectedExtraFields)
                ? updateFieldParents(
                    tempFields.selectedExtraFields,
                    field,
                    nextParents,
                  )
                : tempFields.selectedExtraFields,
            },
          },
        };
      };

      setCurrentProcessingFile(updateParents);
      setDocCurrentProcessingFile(updateParents);
    },
    [setCurrentProcessingFile, setDocCurrentProcessingFile],
  );

  const handleGroupDefaultChange = useCallback(
    async (field, defaultOptionIds) => {
      const catalogField = fields?.find(
        (candidate) => candidate?.field_id === field?.field_id,
      );

      if (!catalogField) {
        throw new Error(
          "The group field could not be found in the field catalog.",
        );
      }

      const normalizedDefaults = [
        ...new Set((defaultOptionIds || []).filter(Boolean)),
      ];
      const savedDefaults =
        catalogField?.field_type === "selectGroup" && catalogField?.multiple
          ? normalizedDefaults
          : normalizedDefaults.slice(0, 1);
      const fieldPayload = {
        ...catalogField,
        def_value: savedDefaults,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/upsert`,
        {
          currentSession,
          field: fieldPayload,
        },
        { withCredentials: true },
      );

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message || "Unable to save the default value.",
        );
      }

      const updateDefault = (previous) => ({
        ...previous,
        document: {
          ...previous.document,
          tempFields: {
            ...previous.document.tempFields,
            formFields: updatePreviewGroupDefault(
              previous.document.tempFields.formFields,
              field,
              savedDefaults,
            ),
          },
        },
      });

      setCurrentProcessingFile(updateDefault);
      setDocCurrentProcessingFile(updateDefault);
    },
    [
      currentSession,
      fields,
      setCurrentProcessingFile,
      setDocCurrentProcessingFile,
    ],
  );

  // const handleConnectionCommit = useCallback(
  //   (updatedFields) => {
  //     const replaceFields = (previous) => ({
  //       ...previous,
  //       document: {
  //         ...previous.document,
  //         tempFields: {
  //           ...previous.document.tempFields,
  //           formFields: updatedFields,
  //         },
  //       },
  //     });

  //     setCurrentProcessingFile(replaceFields);
  //     setDocCurrentProcessingFile(replaceFields);
  //   },
  //   [setCurrentProcessingFile, setDocCurrentProcessingFile],
  // );

  const handleFillDemo = useCallback(() => {
    mapFormFields((f) => {
      if (isTextConnectedField(f)) {
        return {
          ...f,
          text_connection: {
            ...f.text_connection,
            source_value: "Sample text for the connected fields.",
          },
        };
      }

      if (isConnectedCharacterField(f)) {
        const source = allFormField.find(
          (item) =>
            isConnectionSourceField(item) &&
            item.field_id === f.connection.source_field_id,
        );
        const demo = Array.from(
          {
            length: source?.connection_capacity || f.connection.character_count,
          },
          (_, i) => String((i + 1) % 10),
        ).join("");
        return {
          ...f,
          value: demo[f.connection.character_index] || "",
          connection: { ...f.connection, source_value: demo },
        };
      }

      if (isGenericTablePreviewField(f)) {
        const genericFieldType =
          f?.generic_table?.field_type || f?.field_type || "text";
        const demoField = {
          ...f,
          name:
            f?.generic_table?.column_name ||
            f?.generic_table?.table_label ||
            f?.name,
          label: [
            f?.generic_table?.table_label,
            f?.generic_table?.row_name,
            f?.generic_table?.column_name,
          ]
            .filter(Boolean)
            .join(" "),
        };

        return {
          ...f,
          value: getDemoValue(demoField, {
            field_type: genericFieldType,
          }),
        };
      }

      const formField = allFormField?.find(
        (field) => field?.field_id === f.field_id,
      );
      const childField = allFormField?.find((field) =>
        field?.options?.some((o) => o.value === f.field_id),
      );

      if (SKIP_TYPES.has(f.type) || !(childField || formField)) return f;

      if (
        childField?.field_type === "selectGroup" ||
        childField?.field_type === "checkboxGroup"
      ) {
        const isFirstOpt = childField.options?.[0]?.value === f.field_id;
        return { ...f, value: isFirstOpt };
      }

      return {
        ...f,
        value: getDemoValue(
          { ...f, label: f?.field_label },
          formField ?? childField,
        ),
      };
    });
    setDemoVersion((v) => v + 1);
  }, [mapFormFields, allFormField]);

  const handleClear = useCallback(() => {
    mapFormFields((f) => {
      if (isTextConnectedField(f)) {
        return {
          ...f,
          value: "",
          text_connection: {
            ...f.text_connection,
            source_value: "",
          },
        };
      }
      if (isConnectedCharacterField(f))
        return {
          ...f,
          value: "",
          connection: { ...f.connection, source_value: "" },
        };

      if (SKIP_TYPES.has(f.type)) return f;
      return {
        ...f,
        value:
          f.type === "PDFCheckBox" || f.type === "PDFRadioGroup" ? false : "",
      };
    });
    setDemoVersion((v) => v + 1);
  }, [mapFormFields]);

  const withReorderedFields = (prev, reorderedFields) => ({
    ...prev,
    document: {
      ...prev.document,
      tempFields: { ...prev.document.tempFields, formFields: reorderedFields },
    },
  });

  const handleApplyOptionOrder = useCallback(
    (parentField, reorderedValues) => {
      const reorder = (prev) => {
        const rawFields = prev.document.tempFields.formFields ?? [];

        const matchingRawFields = reorderedValues
          .map((optionValue) =>
            rawFields.find((f) => f.field_id === optionValue),
          )
          .filter(Boolean);

        // Options without separate PDF widgets:
        // store the order on the group itself.
        if (matchingRawFields.length !== reorderedValues.length) {
          const optionsByValue = new Map(
            (parentField.options || []).map((option) => [option.value, option]),
          );

          const reorderedOptions = reorderedValues.map((value, index) => ({
            ...optionsByValue.get(value),
            order: index,
          }));

          let changed = false;

          const reorderedFields = rawFields.map((field) => {
            const isMatchingGroup =
              (parentField.field_id &&
                field.field_id === parentField.field_id) ||
              (parentField.name && field.name === parentField.name);

            if (!isMatchingGroup) return field;

            changed = true;

            return {
              ...field,
              options: reorderedOptions,
            };
          });

          return changed ? withReorderedFields(prev, reorderedFields) : prev;
        }

        // Existing behavior for options with separate PDF widgets.
        const hasValidOrders = matchingRawFields.every((f) =>
          Number.isFinite(Number(f.order)),
        );
        const existingOrders = hasValidOrders
          ? matchingRawFields.map((f) => Number(f.order)).sort((a, b) => a - b)
          : matchingRawFields.map((_, i) => i);

        const orderByFieldId = new Map(
          matchingRawFields.map((f, i) => [f.field_id, existingOrders[i]]),
        );

        const reorderedFields = rawFields.map((f) =>
          orderByFieldId.has(f.field_id)
            ? { ...f, order: orderByFieldId.get(f.field_id) }
            : f,
        );

        return withReorderedFields(prev, reorderedFields);
      };

      setDocCurrentProcessingFile(reorder);
      setCurrentProcessingFile(reorder);
    },
    [setCurrentProcessingFile, setDocCurrentProcessingFile],
  );

  const handleOptionDragEnd = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return;

      const activeValue = getOptionValueFromSortId(active.id);
      const overValue = getOptionValueFromSortId(over.id);

      const parentField = allFormField?.find((field) =>
        field?.options?.some((option) => option.value === activeValue),
      );
      if (!parentField) return;

      const optionValues = dedupeOptionsByValue(parentField.options || []).map(
        (option) => option.value,
      );

      const from = optionValues.indexOf(activeValue);
      const to = optionValues.indexOf(overValue);
      if (from < 0 || to < 0) return;

      handleApplyOptionOrder(parentField, arrayMove(optionValues, from, to));
    },
    [allFormField, handleApplyOptionOrder],
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      if (isOptionSortId(active.id) || isOptionSortId(over.id)) {
        handleOptionDragEnd(event);
        return;
      }

      const sortFields = (prev) => {
        const rawFields = prev.document.tempFields.formFields ?? [];

        const sortableRawFields = rawFields
          .filter(isRightPreviewFormField)
          .filter((field) => !isCanvasAreaField(field))
          .filter((field) => !isGenericTablePreviewField(field));

        const { allFields } = getSections(sortableRawFields, fields);
        const sortedRenderableFields = dedupeRenderableFields(allFields).sort(
          (a, b) => getFieldOrder(a) - getFieldOrder(b),
        );
        const units = buildRowUnits(sortedRenderableFields);

        const isFieldId = (id) =>
          typeof id === "string" && id.startsWith("field:");
        const isUnitId = (id) =>
          typeof id === "string" &&
          (id.startsWith("row:") || id.startsWith("unit:"));

        const getOwnerUnitId = (id) => {
          if (isUnitId(id)) return id;

          if (!isFieldId(id)) return null;

          const ownerUnit = units.find((unit) =>
            unit.fields.some((field) => getFieldSortId(field) === id),
          );

          return ownerUnit?.id ?? null;
        };

        const rawBlockByRenderedFieldId = new Map(
          sortedRenderableFields.map((field) => {
            const rawIds = getRenderedFieldRawIds(field);
            const linkedRawFields = rawFields
              .filter((rawField) => rawIds.has(rawField.field_id))
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

            return [getFieldSortId(field), linkedRawFields];
          }),
        );

        const flattenUnitsToRaw = (unitList) => {
          const representedIds = new Set();

          const orderedRawFields = unitList.flatMap((unit) =>
            unit.fields.flatMap((field) => {
              const linkedRawFields =
                rawBlockByRenderedFieldId.get(getFieldSortId(field)) ?? [];

              linkedRawFields.forEach((rawField) => {
                representedIds.add(rawField.field_id);
              });

              return linkedRawFields;
            }),
          );

          const untouchedRawFields = rawFields
            .filter((rawField) => !representedIds.has(rawField.field_id))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

          return [...orderedRawFields, ...untouchedRawFields].map(
            (field, index) => ({
              ...field,
              order: index,
            }),
          );
        };

        const activeUnitId = getOwnerUnitId(active.id);
        const overUnitId = getOwnerUnitId(over.id);

        if (activeUnitId && overUnitId && activeUnitId !== overUnitId) {
          const activeIndex = units.findIndex((u) => u.id === activeUnitId);
          const overIndex = units.findIndex((u) => u.id === overUnitId);
          if (activeIndex === -1 || overIndex === -1) return prev;

          return withReorderedFields(
            prev,
            flattenUnitsToRaw(arrayMove(units, activeIndex, overIndex)),
          );
        }

        if (!isFieldId(active.id) || !isFieldId(over.id)) return prev;

        const ownerUnit = units.find(
          (u) =>
            u.type === "row" &&
            u.fields.some((f) => getFieldSortId(f) === active.id),
        );
        if (!ownerUnit) return prev;
        const fromIdx = ownerUnit.fields.findIndex(
          (f) => getFieldSortId(f) === active.id,
        );
        const toIdx = ownerUnit.fields.findIndex(
          (f) => getFieldSortId(f) === over.id,
        );
        if (fromIdx === -1 || toIdx === -1) return prev;

        const reorderedRowFields = arrayMove(ownerUnit.fields, fromIdx, toIdx);
        const newUnits = units.map((u) =>
          u === ownerUnit ? { ...u, fields: reorderedRowFields } : u,
        );
        return withReorderedFields(prev, flattenUnitsToRaw(newUnits));
      };

      setDocCurrentProcessingFile(sortFields);
      setCurrentProcessingFile(sortFields);
    },
    [
      fields,
      setCurrentProcessingFile,
      setDocCurrentProcessingFile,
      handleOptionDragEnd,
    ],
  );

  const previewBlocks = useMemo(() => {
    const rawFields =
      currentProcessingFile?.document?.tempFields?.formFields ?? [];

    const blocks = sections
      .filter((section) => section.fields.length)
      .map((section) => ({
        type: "section",
        id: getSectionSortId(section.section),
        section,
      }));

    const tableGroups = new Map();
    rawFields.filter(isGenericTablePreviewField).forEach((field) => {
      const key = getGenericTableKey(field);
      if (!tableGroups.has(key)) tableGroups.set(key, []);
      tableGroups.get(key).push(field);
    });

    if (!tableGroups.size) return blocks;

    const getSectionOrder = (section) => {
      const ids = new Set();
      section.fields.forEach((f) =>
        getRenderedFieldRawIds(f).forEach((id) => ids.add(id)),
      );
      const orders = rawFields
        .filter((r) => !isGenericTablePreviewField(r) && ids.has(r.field_id))
        .map((r) => Number(r.order ?? 0));
      return orders.length ? Math.min(...orders) : Infinity;
    };

    [...tableGroups.entries()]
      .map(([key, fields]) => {
        const orders = fields
          .map((f) => Number(f.order))
          .filter(Number.isFinite);
        return {
          type: "generic",
          id: getGenericTableSortId(key),
          fields,
          label: fields[0]?.generic_table?.table_label || "Table",
          order: orders.length ? Math.min(...orders) : Infinity,
        };
      })
      .sort((a, b) => a.order - b.order)
      .forEach((tableBlock) => {
        const insertAt = blocks.findIndex((b) =>
          b.type === "section"
            ? getSectionOrder(b.section) > tableBlock.order
            : b.order > tableBlock.order,
        );
        blocks.splice(
          insertAt === -1 ? blocks.length : insertAt,
          0,
          tableBlock,
        );
      });

    return blocks;
  }, [sections, currentProcessingFile?.document?.tempFields?.formFields]);

  const handleSectionDragEnd = useCallback(
    (event) => {
      setActiveSectionId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeIndex = previewBlocks.findIndex((b) => b.id === active.id);
      const overIndex = previewBlocks.findIndex((b) => b.id === over.id);
      if (activeIndex === -1 || overIndex === -1) return;

      const reorderedBlocks = arrayMove(previewBlocks, activeIndex, overIndex);

      const sortSections = (prev) => {
        const rawFields = prev.document.tempFields.formFields ?? [];
        const rawFieldBlockIds = new Map();

        previewBlocks.forEach((block) => {
          if (block.type !== "section") return;
          block.section.fields.forEach((field) => {
            getRenderedFieldRawIds(field).forEach((rawFieldId) => {
              rawFieldBlockIds.set(rawFieldId, block.id);
            });
          });
        });

        const rawFieldsByBlock = new Map(
          previewBlocks.map((block) => [block.id, []]),
        );
        const untouchedRawFields = [];

        rawFields
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .forEach((rawField) => {
            const blockId = isGenericTablePreviewField(rawField)
              ? getGenericTableSortId(getGenericTableKey(rawField))
              : rawFieldBlockIds.get(rawField.field_id);
            const blockFields = rawFieldsByBlock.get(blockId);

            if (blockFields) blockFields.push(rawField);
            else untouchedRawFields.push(rawField);
          });

        const reorderedRawFields = reorderedBlocks.flatMap(
          (block) => rawFieldsByBlock.get(block.id) ?? [],
        );

        return withReorderedFields(
          prev,
          [...reorderedRawFields, ...untouchedRawFields].map(
            (field, index) => ({
              ...field,
              order: index,
            }),
          ),
        );
      };

      setDocCurrentProcessingFile(sortSections);
      setCurrentProcessingFile(sortSections);
    },
    [previewBlocks, setCurrentProcessingFile, setDocCurrentProcessingFile],
  );

  useEffect(() => {
    if (fillDemoRef) fillDemoRef.current = handleFillDemo;
  }, [fillDemoRef, handleFillDemo]);

  useEffect(() => {
    if (clearRef) clearRef.current = handleClear;
  }, [clearRef, handleClear]);

  const totalFieldCount =
    allFormField.filter(isConnectionSourceField).length +
    sections.reduce((acc, s) => acc + s.fields.length, 0) +
    directItems.length +
    (currentProcessingFile?.document?.tempFields?.formFields || []).filter(
      isGenericTablePreviewField,
    ).length;
  const sectionSortIds = previewBlocks.map((block) => block.id);
  const activeBlock = previewBlocks.find(
    (block) => block.id === activeSectionId,
  );

  if (loading) {
    return (
      <Box sx={{ px: 1.5, display: "flex", justifyContent: "center", mt: 5.5 }}>
        <CircularProgress color="warning" size="4rem" />
      </Box>
    );
  }

  return (
    <OptionReorderContext.Provider value={handleApplyOptionOrder}>
      <Stack
        ref={scrollContainerRef}
        spacing={1.5}
        py={2}
        px={1}
        sx={{
          backgroundColor: "#fff",
          overflowY: "auto",
          height: "calc(100% - 130px)",
        }}
        onMouseOver={(event) => {
          if (!locateFields) return;
          const fieldKey = event.target
            .closest?.("[data-preview-field-key]")
            ?.getAttribute("data-preview-field-key");
          onHoverField?.(
            allFormField.find((field) => getFieldKey(field) === fieldKey),
          );
        }}
        onMouseLeave={() => {
          if (locateFields) onHoverField?.(null);
        }}
      >
        {allFormField.filter(isConnectionSourceField).map((field) => (
          <PreviewCharacterInput
            key={field.name}
            field={field}
            onCommit={handleFieldChange}
            disabled={locateFields}
            demoVersion={demoVersion}
          />
        ))}
        <DndContext
          sensors={sectionSensors}
          collisionDetection={closestCenter}
          onDragStart={({ active }) => setActiveSectionId(active.id)}
          onDragEnd={handleSectionDragEnd}
          onDragCancel={() => setActiveSectionId(null)}
        >
          <SortableContext
            items={sectionSortIds}
            strategy={verticalListSortingStrategy}
          >
            <Stack spacing={1.5}>
              {previewBlocks.map((block) =>
                block.type === "generic" ? (
                  <SortableBlock
                    key={block.id}
                    id={block.id}
                    title={block.label}
                    enableSorting={enableSorting}
                  >
                    <GenericTablePreview
                      fields={block.fields}
                      onCellChange={handleGenericTableCellChange}
                      onTableNameChange={handleGenericTableNameChange}
                      locateFields={locateFields}
                      rowSelectedFieldKeys={rowSelectedFieldKeys}
                      onToggleRowField={onToggleRowField}
                      onLocateField={onLocateField}
                    />
                  </SortableBlock>
                ) : (
                  <SectionCard
                    key={block.section.section}
                    item={block.section}
                    onFieldChange={handleFieldChange}
                    onFieldLabelChange={handleFieldLabelChange}
                    demoVersion={demoVersion}
                    docFields={
                      currentProcessingFile?.document?.tempFields?.formFields
                    }
                    allFormField={allFormField}
                    handleDragEnd={handleDragEnd}
                    enableSorting={enableSorting}
                    sectionDragActive={Boolean(activeSectionId)}
                    locateFields={locateFields}
                    locatedField={locatedField}
                    onLocateField={onLocateField}
                    rowSelectedFieldKeys={rowSelectedFieldKeys}
                    onToggleRowField={onToggleRowField}
                    enableFieldDetial={fieldDetails}
                    onParentsChange={handleFieldParentsChange}
                    onDefaultChange={handleGroupDefaultChange}
                  />
                ),
              )}
            </Stack>
          </SortableContext>
          <DragOverlay adjustScale={false}>
            {activeBlock ? (
              <Box
                sx={{
                  width: "min(420px, calc(100vw - 32px))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  backgroundColor: "#fff",
                  border: "2px solid",
                  borderColor: "primary.main",
                  borderRadius: "15px",
                  px: 2,
                  py: 1.25,
                  boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.28)",
                  cursor: "grabbing",
                }}
              >
                <DragIndicatorIcon
                  sx={{
                    position: "absolute",
                    left: 16,
                    fontSize: "1.4rem",
                    fill: "#ababab",
                  }}
                />
                <Typography variant="h6" align="center" color="primary">
                  {activeBlock.type === "generic"
                    ? activeBlock.label
                    : getPreviewSectionTitle(activeBlock.section)}
                </Typography>
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>

        <DirectFieldCard
          fields={directItems}
          onFieldChange={handleFieldChange}
          onFieldLabelChange={handleFieldLabelChange}
          demoVersion={demoVersion}
          docFields={currentProcessingFile?.document?.tempFields?.formFields}
          allFormField={allFormField}
          handleDragEnd={handleDragEnd}
          enableSorting={enableSorting}
          locateFields={locateFields}
          locatedField={locatedField}
          onLocateField={onLocateField}
          rowSelectedFieldKeys={rowSelectedFieldKeys}
          onToggleRowField={onToggleRowField}
          enableFieldDetial={fieldDetails}
          onParentsChange={handleFieldParentsChange}
          onDefaultChange={handleGroupDefaultChange}
        />

        {totalFieldCount === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 8,
              gap: 1,
              color: "text.disabled",
            }}
          >
            <Typography variant="h6">No form fields found</Typography>
            <Typography variant="body2">
              Fields will appear here once the PDF is processed.
            </Typography>
          </Box>
        )}
      </Stack>
    </OptionReorderContext.Provider>
  );
};

export default PreviewForm;
